/**
 * 🎯 WELL ASSIGNMENT SERVICE
 * 
 * Enterprise-grade well assignment management with full tenant isolation.
 * This is the SINGLE SOURCE OF TRUTH for all assignment operations.
 * 
 * Features:
 * - Single & bulk well assignment
 * - Primary/backup pumper management
 * - Temporary coverage system
 * - Assignment history tracking
 * - Workload calculations
 * - Orphan well detection
 * 
 * CRITICAL RULES (from copilot-instructions.md):
 * 1. Always use guardedCreate/guardedUpdate/guardedDelete
 * 2. Keep assignedPumperId synced with assignedPumperIds[0]
 * 3. Create assignment_history entries on every change
 * 4. Send notifications with reason when provided
 * 5. Respect wellVisibility.mode when querying
 * 6. Handle batch limits (500 max)
 * 
 * @module wellAssignmentService
 * @version 1.0.0
 * @security CRITICAL - Enforces multi-tenant data isolation
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  writeBatch,
  serverTimestamp,
  QueryConstraint,
} from 'firebase/firestore';
import { db } from '../firebase/client';
import { FIRESTORE_PATHS, generateHistoryEntryId } from '../firebase/paths';
import { guardedCreate, guardedUpdate } from '../firebase/writeGuard';
import { getCompanyId } from '../tenantContext';
import { getUsersByCompany } from '../auth/userService';

// Import types from wellAssignment.ts
import type {
  WellVisibilitySettings,
  AssignmentHistoryEntry,
  AssignmentAction,
  HistoryMethod,
  TemporaryAssignment,
  TemporaryAssignmentDocument,
  TemporaryAssignmentStatus,
  CoverageReason,
  CoverageEndReason,
} from '../../types/wellAssignment';

import {
  DEFAULT_WELL_VISIBILITY_SETTINGS,
  createUnassignmentFields,
} from '../../types/wellAssignment';

// Import notification functions (Tasks 65-75)
import {
  notifyPumperOfAssignment,
  notifyPumperOfUnassignment,
  notifyPumperOfBulkAssignment,
  notifyCoverageStarted,
  notifyCoverageEnded,
  notifyBackupActivated,
  notifyBackupDeactivated,
} from '../notifications/notificationService';

// ═══════════════════════════════════════════════════════════════════════════════
// TYPE DEFINITIONS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Result of an assignment operation
 */
export interface AssignmentResult {
  success: boolean;
  wellId: string;
  wellName?: string;
  error?: string;
}

/**
 * Result of a bulk assignment operation
 */
export interface BulkAssignmentResult {
  success: boolean;
  totalWells: number;
  successCount: number;
  failureCount: number;
  results: AssignmentResult[];
  /** Wells that failed */
  failures: AssignmentResult[];
}

/**
 * Options for assignment operations
 */
export interface AssignmentOptions {
  /** Reason for assignment (shown to pumper) */
  reason?: string;
  /** Additional notes (internal) */
  notes?: string;
  /** User ID performing the action */
  performedBy: string;
  /** User name performing the action */
  performedByName?: string;
  /** Skip notification (for batch operations) */
  skipNotification?: boolean;
  /** Is this a primary or backup assignment */
  assignmentType?: 'primary' | 'backup';
}

/**
 * Options for unassignment operations
 */
export interface UnassignmentOptions {
  /** Reason for unassignment */
  reason?: string;
  /** User ID performing the action */
  performedBy: string;
  /** User name performing the action */
  performedByName?: string;
  /** Skip notification (respects company settings) */
  skipNotification?: boolean;
}

/**
 * Well data needed for assignment operations
 */
export interface WellForAssignment {
  id: string;
  name: string;
  companyId: string;
  assignedPumperIds: string[];
  assignedPumperId?: string;
  primaryPumperId?: string;
  primaryPumperName?: string;
  backupPumperId?: string;
  backupPumperName?: string;
  status: string;
}

/**
 * Pumper data for assignment operations
 */
export interface PumperForAssignment {
  id: string;
  name: string;
  email?: string;
}

// ═══════════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════════

/** Maximum batch size for Firestore operations */
const FIRESTORE_BATCH_LIMIT = 500;

/** Maximum wells per bulk assignment (safety limit) */
const MAX_BULK_ASSIGNMENT_WELLS = 200;

// ═══════════════════════════════════════════════════════════════════════════════
// WELL VISIBILITY SETTINGS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Get company well visibility settings.
 * Creates default settings if they don't exist.
 * 
 * @param companyId - Company ID (uses current context if not provided)
 * @returns WellVisibilitySettings
 */
export async function getWellVisibilitySettings(
  companyId?: string
): Promise<WellVisibilitySettings> {
  const cid = companyId || getCompanyId();
  const path = FIRESTORE_PATHS.wellVisibilitySettings(cid);
  
  try {
    const docRef = doc(db, path);
    const snapshot = await getDoc(docRef);
    
    if (snapshot.exists()) {
      const data = snapshot.data();
      return {
        mode: data.mode || 'assigned',
        notifyOnUnassign: data.notifyOnUnassign ?? true,
        notifyOnCoverageEnd: data.notifyOnCoverageEnd ?? true,
        companyId: cid,
        updatedAt: data.updatedAt?.toDate() || new Date(),
        updatedBy: data.updatedBy || 'system',
        createdAt: data.createdAt?.toDate() || new Date(),
        createdBy: data.createdBy || 'system',
      };
    }
    
    // Return defaults if document doesn't exist
    // (Document will be created on first save)
    return {
      ...DEFAULT_WELL_VISIBILITY_SETTINGS,
      companyId: cid,
      updatedAt: new Date(),
      updatedBy: 'system',
      createdAt: new Date(),
      createdBy: 'system',
    };
  } catch (error) {
    console.error('Error getting well visibility settings:', error);
    // Return safe defaults on error
    return {
      ...DEFAULT_WELL_VISIBILITY_SETTINGS,
      companyId: cid,
      updatedAt: new Date(),
      updatedBy: 'system',
      createdAt: new Date(),
      createdBy: 'system',
    };
  }
}

/**
 * Update company well visibility settings.
 * Creates the document if it doesn't exist.
 * 
 * @param settings - Partial settings to update
 * @param userId - User making the change
 * @returns Updated settings
 */
export async function setWellVisibilitySettings(
  settings: Partial<Pick<WellVisibilitySettings, 'mode' | 'notifyOnUnassign' | 'notifyOnCoverageEnd'>>,
  userId: string
): Promise<WellVisibilitySettings> {
  const companyId = getCompanyId();
  const path = FIRESTORE_PATHS.companySettings(companyId);
  const docId = 'wellVisibility';
  
  // Get current settings to check if document exists
  const current = await getWellVisibilitySettings(companyId);
  const docRef = doc(db, path, docId);
  const snapshot = await getDoc(docRef);
  
  const updateData = {
    ...settings,
    companyId,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  };
  
  if (snapshot.exists()) {
    // Update existing
    await guardedUpdate(path, docId, updateData, { userId });
  } else {
    // Create new with defaults
    await guardedCreate(path, docId, {
      ...DEFAULT_WELL_VISIBILITY_SETTINGS,
      ...settings,
      createdBy: userId,
    }, { userId });
  }
  
  console.log(`✅ Updated well visibility settings for ${companyId}`);
  
  // Return merged settings
  return {
    ...current,
    ...settings,
    updatedAt: new Date(),
    updatedBy: userId,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// BACKWARD COMPATIBILITY HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Ensures assignedPumperId stays in sync with assignedPumperIds[0].
 * CRITICAL: This must be called on every assignment change.
 * 
 * @param assignedPumperIds - The array of assigned pumper IDs
 * @returns Object with synced fields
 */
export function syncAssignmentFields(
  assignedPumperIds: string[]
): { assignedPumperIds: string[]; assignedPumperId: string | null } {
  return {
    assignedPumperIds,
    assignedPumperId: assignedPumperIds[0] || null,
  };
}

/**
 * Lazy migration: If well has assignedPumperId but no assignedPumperIds,
 * migrate to array format on read.
 * 
 * @param wellData - Raw well data from Firestore
 * @returns Migrated well data
 */
export function migrateWellAssignmentFields(
  wellData: Record<string, unknown>
): WellForAssignment {
  const assignedPumperId = wellData.assignedPumperId as string | undefined;
  let assignedPumperIds = wellData.assignedPumperIds as string[] | undefined;
  
  // Migrate legacy single field to array
  if (assignedPumperId && (!assignedPumperIds || assignedPumperIds.length === 0)) {
    assignedPumperIds = [assignedPumperId];
    console.log(`🔄 Migrated well ${wellData.id} from assignedPumperId to assignedPumperIds`);
  }
  
  // Ensure array exists
  if (!assignedPumperIds) {
    assignedPumperIds = [];
  }
  
  return {
    id: wellData.id as string,
    name: wellData.name as string,
    companyId: wellData.companyId as string,
    assignedPumperIds,
    assignedPumperId: assignedPumperIds[0] || undefined,
    primaryPumperId: wellData.primaryPumperId as string | undefined,
    primaryPumperName: wellData.primaryPumperName as string | undefined,
    backupPumperId: wellData.backupPumperId as string | undefined,
    backupPumperName: wellData.backupPumperName as string | undefined,
    status: wellData.status as string || 'active',
  };
}

/**
 * Persist migration if well data was in legacy format.
 * Called after read if migration was needed.
 */
export async function persistMigrationIfNeeded(
  wellId: string,
  originalData: Record<string, unknown>,
  userId: string
): Promise<boolean> {
  const assignedPumperId = originalData.assignedPumperId as string | undefined;
  const assignedPumperIds = originalData.assignedPumperIds as string[] | undefined;
  
  // Check if migration needed
  if (assignedPumperId && (!assignedPumperIds || assignedPumperIds.length === 0)) {
    const companyId = getCompanyId();
    const path = FIRESTORE_PATHS.wells(companyId);
    
    await guardedUpdate(path, wellId, {
      assignedPumperIds: [assignedPumperId],
      backupActive: originalData.backupActive ?? false,
    }, { userId });
    
    console.log(`✅ Persisted migration for well ${wellId}`);
    return true;
  }
  
  return false;
}

// ═══════════════════════════════════════════════════════════════════════════════
// ASSIGNMENT HISTORY
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Create an assignment history entry.
 * Called automatically by all assignment operations.
 * 
 * @param wellId - Well being modified
 * @param wellName - Well name (for denormalization)
 * @param action - The action being performed
 * @param options - Additional data for the history entry
 */
export async function createAssignmentHistoryEntry(
  wellId: string,
  wellName: string,
  action: AssignmentAction,
  options: {
    /** IDs of pumpers affected (added, removed, or changed) */
    pumperIds: string[];
    /** Names of affected pumpers for display */
    pumperNames: string[];
    /** How the assignment was made */
    method: HistoryMethod;
    /** Route ID if this was route-based */
    routeId?: string;
    routeName?: string;
    /** Optional reason provided by manager */
    reason?: string;
    /** User who performed the action */
    performedBy: string;
    performedByName: string;
    /** Extra metadata */
    metadata?: Record<string, unknown>;
  }
): Promise<string> {
  const companyId = getCompanyId();
  const historyId = generateHistoryEntryId(action);
  const path = FIRESTORE_PATHS.wellAssignmentHistory(companyId, wellId);
  
  // Build the history entry to match AssignmentHistoryEntry interface
  const historyEntry = {
    wellId,
    action,
    pumperIds: options.pumperIds,
    pumperNames: options.pumperNames,
    performedBy: options.performedBy,
    performedByName: options.performedByName,
    method: options.method,
    routeId: options.routeId,
    routeName: options.routeName,
    reason: options.reason,
    timestamp: serverTimestamp(),
    createdBy: options.performedBy,
    metadata: options.metadata,
  };
  
  await guardedCreate(path, historyId, historyEntry, { userId: options.performedBy });
  
  console.log(`📜 Created history entry: ${action} on well ${wellName}`);
  return historyId;
}

/**
 * Get assignment history for a well.
 * 
 * @param wellId - Well ID
 * @param options - Query options
 * @returns Array of history entries
 */
export async function getAssignmentHistory(
  wellId: string,
  options: {
    limit?: number;
    action?: AssignmentAction;
  } = {}
): Promise<AssignmentHistoryEntry[]> {
  const companyId = getCompanyId();
  const path = FIRESTORE_PATHS.wellAssignmentHistory(companyId, wellId);
  
  const constraints: QueryConstraint[] = [
    orderBy('timestamp', 'desc'),
  ];
  
  if (options.action) {
    constraints.unshift(where('action', '==', options.action));
  }
  
  if (options.limit) {
    constraints.push(firestoreLimit(options.limit));
  }
  
  const q = query(collection(db, path), ...constraints);
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      id: doc.id,
      companyId: data.companyId ?? companyId,
      wellId: data.wellId,
      action: data.action as AssignmentAction,
      pumperIds: data.pumperIds ?? [],
      pumperNames: data.pumperNames ?? [],
      performedBy: data.performedBy,
      performedByName: data.performedByName ?? 'Unknown',
      method: data.method as HistoryMethod,
      routeId: data.routeId,
      routeName: data.routeName,
      reason: data.reason,
      timestamp: data.timestamp?.toDate?.() ?? new Date(),
      createdAt: data.createdAt?.toDate?.() ?? new Date(),
      createdBy: data.createdBy ?? data.performedBy,
      metadata: data.metadata,
    } as AssignmentHistoryEntry;
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
// SINGLE WELL ASSIGNMENT
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Assign a well to a pumper.
 * 
 * This is the PRIMARY assignment function. It:
 * 1. Updates the well document with new assignment
 * 2. Syncs assignedPumperId with assignedPumperIds[0]
 * 3. Creates assignment history entry
 * 4. Triggers notification (unless skipped)
 * 
 * @param wellId - Well to assign
 * @param wellName - Well name (for history/notifications)
 * @param pumper - Pumper being assigned
 * @param options - Assignment options
 * @returns Assignment result
 */
export async function assignWellToPumper(
  wellId: string,
  wellName: string,
  pumper: PumperForAssignment,
  options: AssignmentOptions
): Promise<AssignmentResult> {
  const companyId = getCompanyId();
  const path = FIRESTORE_PATHS.wells(companyId);
  
  try {
    // Get current well state for history
    const wellRef = doc(db, path, wellId);
    const wellSnap = await getDoc(wellRef);
    
    if (!wellSnap.exists()) {
      return {
        success: false,
        wellId,
        wellName,
        error: `Well ${wellId} not found`,
      };
    }
    
    const currentData = migrateWellAssignmentFields(wellSnap.data());
    const previousPumperId = currentData.primaryPumperId || currentData.assignedPumperIds[0] || null;
    const previousPumperName = currentData.primaryPumperName;
    
    // Build update based on assignment type
    const isBackupAssignment = options.assignmentType === 'backup';
    
    let updateData: Record<string, unknown>;
    
    if (isBackupAssignment) {
      // Backup assignment - doesn't change primary or assignedPumperIds
      updateData = {
        backupPumperId: pumper.id,
        backupPumperName: pumper.name,
      };
    } else {
      // Primary assignment
      const newAssignedIds = [pumper.id];
      // Keep backup if exists and different from new primary
      if (currentData.backupPumperId && currentData.backupPumperId !== pumper.id) {
        newAssignedIds.push(currentData.backupPumperId);
      }
      
      updateData = {
        ...syncAssignmentFields(newAssignedIds),
        primaryPumperId: pumper.id,
        primaryPumperName: pumper.name,
        // Initialize backupActive if not set
        backupActive: currentData.backupPumperId ? false : false,
      };
    }
    
    // Update well document
    await guardedUpdate(path, wellId, updateData, { userId: options.performedBy });
    
    // Create history entry
    await createAssignmentHistoryEntry(wellId, wellName, 'assigned', {
      pumperIds: [pumper.id],
      pumperNames: [pumper.name],
      method: 'manual', // single well assignment
      reason: options.reason,
      performedBy: options.performedBy,
      performedByName: options.performedByName || 'System',
      metadata: {
        assignmentType: options.assignmentType || 'primary',
        previousPumperId,
        previousPumperName,
      },
    });
    
    // Send notification (Task 65)
    if (!options.skipNotification) {
      await notifyPumperOfAssignment(
        pumper.id,
        pumper.name,
        { id: wellId, name: wellName },
        {
          reason: options.reason,
          performedBy: options.performedBy,
          performedByName: options.performedByName || 'System',
          assignmentType: options.assignmentType || 'primary',
        }
      );
    }
    
    console.log(`✅ Assigned well ${wellName} to ${pumper.name}`);
    
    return {
      success: true,
      wellId,
      wellName,
    };
  } catch (error) {
    console.error(`❌ Failed to assign well ${wellId}:`, error);
    return {
      success: false,
      wellId,
      wellName,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Unassign a well from a pumper.
 * 
 * @param wellId - Well to unassign
 * @param wellName - Well name (for history/notifications)
 * @param pumperId - Pumper being removed (optional - if not provided, removes all)
 * @param options - Unassignment options
 * @returns Assignment result
 */
export async function unassignWellFromPumper(
  wellId: string,
  wellName: string,
  pumperId: string | null,
  options: UnassignmentOptions
): Promise<AssignmentResult> {
  const companyId = getCompanyId();
  const path = FIRESTORE_PATHS.wells(companyId);
  
  try {
    // Get current well state
    const wellRef = doc(db, path, wellId);
    const wellSnap = await getDoc(wellRef);
    
    if (!wellSnap.exists()) {
      return {
        success: false,
        wellId,
        wellName,
        error: `Well ${wellId} not found`,
      };
    }
    
    const currentData = migrateWellAssignmentFields(wellSnap.data());
    const previousPumperId = currentData.primaryPumperId || currentData.assignedPumperIds[0] || null;
    const previousPumperName = currentData.primaryPumperName;
    
    let updateData: Record<string, unknown>;
    
    if (pumperId) {
      // Remove specific pumper
      const newAssignedIds = currentData.assignedPumperIds.filter(id => id !== pumperId);
      
      updateData = {
        ...syncAssignmentFields(newAssignedIds),
        // If removing primary, promote backup or clear
        primaryPumperId: currentData.primaryPumperId === pumperId 
          ? (newAssignedIds[0] || null) 
          : currentData.primaryPumperId,
        primaryPumperName: currentData.primaryPumperId === pumperId 
          ? (currentData.backupPumperName || null) 
          : currentData.primaryPumperName,
        // If removing backup, clear backup fields
        ...(currentData.backupPumperId === pumperId && {
          backupPumperId: null,
          backupPumperName: null,
          backupActive: false,
        }),
      };
    } else {
      // Clear all assignments
      updateData = {
        ...createUnassignmentFields(),
      };
    }
    
    // Update well document
    await guardedUpdate(path, wellId, updateData, { userId: options.performedBy });
    
    // Create history entry
    await createAssignmentHistoryEntry(wellId, wellName, 'unassigned', {
      pumperIds: previousPumperId ? [previousPumperId] : [],
      pumperNames: previousPumperName ? [previousPumperName] : [],
      method: 'manual',
      reason: options.reason,
      performedBy: options.performedBy,
      performedByName: options.performedByName || 'System',
      metadata: {
        previousPumperId,
        previousPumperName,
      },
    });
    
    // Send notification (Task 66) - respects notifyOnUnassign setting
    if (!options.skipNotification && previousPumperId) {
      const settings = await getWellVisibilitySettings();
      if (settings.notifyOnUnassign) {
        await notifyPumperOfUnassignment(
          previousPumperId,
          previousPumperName || 'Unknown',
          { id: wellId, name: wellName },
          {
            reason: options.reason,
            performedBy: options.performedBy,
            performedByName: options.performedByName || 'System',
          }
        );
      }
    }
    
    console.log(`✅ Unassigned well ${wellName}`);
    
    return {
      success: true,
      wellId,
      wellName,
    };
  } catch (error) {
    console.error(`❌ Failed to unassign well ${wellId}:`, error);
    return {
      success: false,
      wellId,
      wellName,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// BULK ASSIGNMENT OPERATIONS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Chunk an array into smaller arrays of specified size.
 * Used for respecting Firestore batch limits.
 */
function chunkArray<T>(array: T[], chunkSize: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
}

/**
 * Bulk assign multiple wells to a pumper.
 * 
 * Handles Firestore's 500 operation batch limit by chunking.
 * Each chunk is processed as a separate batch for atomicity within chunk.
 * 
 * @param wells - Array of wells to assign
 * @param pumper - Pumper being assigned
 * @param options - Assignment options
 * @returns Bulk assignment result
 */
export async function bulkAssignWells(
  wells: Array<{ id: string; name: string }>,
  pumper: PumperForAssignment,
  options: AssignmentOptions
): Promise<BulkAssignmentResult> {
  const companyId = getCompanyId();
  
  // Safety limit
  if (wells.length > MAX_BULK_ASSIGNMENT_WELLS) {
    console.warn(`⚠️ Bulk assignment limited to ${MAX_BULK_ASSIGNMENT_WELLS} wells`);
    wells = wells.slice(0, MAX_BULK_ASSIGNMENT_WELLS);
  }
  
  const results: AssignmentResult[] = [];
  const failures: AssignmentResult[] = [];
  
  // Process in chunks to respect batch limits
  // Each well needs ~2 operations (update well + create history)
  const chunkSize = Math.floor(FIRESTORE_BATCH_LIMIT / 2);
  const chunks = chunkArray(wells, chunkSize);
  
  console.log(`📦 Processing ${wells.length} wells in ${chunks.length} batches`);
  
  for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
    const chunk = chunks[chunkIndex];
    const batch = writeBatch(db);
    
    try {
      for (const well of chunk) {
        const wellPath = FIRESTORE_PATHS.well(companyId, well.id);
        const wellRef = doc(db, wellPath);
        
        // Get current state for comparison (not in batch)
        const wellSnap = await getDoc(wellRef);
        const currentData = wellSnap.exists() 
          ? migrateWellAssignmentFields(wellSnap.data()) 
          : null;
        
        if (!currentData) {
          failures.push({
            success: false,
            wellId: well.id,
            wellName: well.name,
            error: 'Well not found',
          });
          continue;
        }
        
        // Build update
        const isBackup = options.assignmentType === 'backup';
        let updateData: Record<string, unknown>;
        
        if (isBackup) {
          updateData = {
            backupPumperId: pumper.id,
            backupPumperName: pumper.name,
            updatedAt: serverTimestamp(),
          };
        } else {
          const newAssignedIds = [pumper.id];
          if (currentData.backupPumperId && currentData.backupPumperId !== pumper.id) {
            newAssignedIds.push(currentData.backupPumperId);
          }
          
          updateData = {
            assignedPumperIds: newAssignedIds,
            assignedPumperId: pumper.id,
            primaryPumperId: pumper.id,
            primaryPumperName: pumper.name,
            backupActive: false,
            updatedAt: serverTimestamp(),
            updatedBy: options.performedBy,
          };
        }
        
        batch.update(wellRef, updateData);
        
        // Create history entry
        const historyId = generateHistoryEntryId('assigned');
        const historyPath = FIRESTORE_PATHS.wellAssignmentHistoryEntry(companyId, well.id, historyId);
        const historyRef = doc(db, historyPath);
        
        batch.set(historyRef, {
          id: historyId,
          companyId,
          wellId: well.id,
          wellName: well.name,
          action: 'assigned',
          previousPumperId: currentData.primaryPumperId || currentData.assignedPumperIds[0] || null,
          previousPumperName: currentData.primaryPumperName,
          newPumperId: pumper.id,
          newPumperName: pumper.name,
          reason: options.reason,
          notes: options.notes,
          performedBy: options.performedBy,
          performedByName: options.performedByName,
          timestamp: serverTimestamp(),
          createdAt: serverTimestamp(),
          metadata: { bulk: true, assignmentType: options.assignmentType || 'primary' },
        });
        
        results.push({
          success: true,
          wellId: well.id,
          wellName: well.name,
        });
      }
      
      // Commit this chunk
      await batch.commit();
      console.log(`✅ Batch ${chunkIndex + 1}/${chunks.length} committed (${chunk.length} wells)`);
      
    } catch (error) {
      console.error(`❌ Batch ${chunkIndex + 1} failed:`, error);
      
      // Mark all wells in failed chunk as failures
      for (const well of chunk) {
        const existingResult = results.find(r => r.wellId === well.id);
        if (existingResult) {
          existingResult.success = false;
          existingResult.error = error instanceof Error ? error.message : 'Batch failed';
          failures.push(existingResult);
        }
      }
    }
  }
  
  // Send single bulk notification (Task 67)
  const successfulResults = results.filter(r => r.success);
  if (!options.skipNotification && successfulResults.length > 0) {
    await notifyPumperOfBulkAssignment(
      pumper.id,
      pumper.name,
      successfulResults.map(r => ({
        wellId: r.wellId,
        wellName: r.wellName || r.wellId,
        success: true,
      })),
      {
        reason: options.reason,
        performedBy: options.performedBy,
        performedByName: options.performedByName || 'System',
        assignmentType: options.assignmentType || 'primary',
      }
    );
  }
  
  const successCount = successfulResults.length;
  const failureCount = failures.length;
  
  console.log(`📊 Bulk assignment complete: ${successCount} success, ${failureCount} failed`);
  
  return {
    success: failureCount === 0,
    totalWells: wells.length,
    successCount,
    failureCount,
    results,
    failures,
  };
}

/**
 * Assign all wells in a route to a pumper.
 * 
 * @param routeId - Route ID
 * @param pumper - Pumper being assigned
 * @param options - Assignment options
 * @returns Bulk assignment result
 */
export async function assignRouteToPumper(
  routeId: string,
  pumper: PumperForAssignment,
  options: AssignmentOptions
): Promise<BulkAssignmentResult> {
  const companyId = getCompanyId();
  
  try {
    // Get route to find wells
    const routePath = FIRESTORE_PATHS.route(companyId, routeId);
    const routeRef = doc(db, routePath);
    const routeSnap = await getDoc(routeRef);
    
    if (!routeSnap.exists()) {
      return {
        success: false,
        totalWells: 0,
        successCount: 0,
        failureCount: 0,
        results: [],
        failures: [{
          success: false,
          wellId: '',
          error: `Route ${routeId} not found`,
        }],
      };
    }
    
    const routeData = routeSnap.data();
    const routeName = routeData.name || routeId;
    const wellIds = routeData.wellIds as string[] || [];
    
    if (wellIds.length === 0) {
      return {
        success: true,
        totalWells: 0,
        successCount: 0,
        failureCount: 0,
        results: [],
        failures: [],
      };
    }
    
    // Get well names for history/notifications
    const wells: Array<{ id: string; name: string }> = [];
    for (const wellId of wellIds) {
      const wellPath = FIRESTORE_PATHS.well(companyId, wellId);
      const wellRef = doc(db, wellPath);
      const wellSnap = await getDoc(wellRef);
      
      if (wellSnap.exists()) {
        wells.push({
          id: wellId,
          name: wellSnap.data().name || wellId,
        });
      }
    }
    
    // Use bulk assign
    const result = await bulkAssignWells(wells, pumper, {
      ...options,
      notes: options.notes || `Assigned from route: ${routeName}`,
    });
    
    console.log(`✅ Route ${routeName} assigned to ${pumper.name}`);
    
    return result;
    
  } catch (error) {
    console.error(`❌ Failed to assign route ${routeId}:`, error);
    return {
      success: false,
      totalWells: 0,
      successCount: 0,
      failureCount: 0,
      results: [],
      failures: [{
        success: false,
        wellId: '',
        error: error instanceof Error ? error.message : 'Unknown error',
      }],
    };
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// ORPHAN WELL DETECTION
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Get active wells with no pumper assigned.
 * These are "orphan" wells that need attention.
 * 
 * @param options - Query options
 * @returns Array of orphan wells
 */
export async function getOrphanWells(
  options: {
    /** Only check wells with these statuses (default: active) */
    statuses?: string[];
    /** Maximum results */
    limit?: number;
  } = {}
): Promise<WellForAssignment[]> {
  const companyId = getCompanyId();
  const path = FIRESTORE_PATHS.wells(companyId);
  
  const statuses = options.statuses || ['active'];
  
  // Query for wells with empty assignedPumperIds or null primaryPumperId
  // Firestore doesn't support empty array queries directly,
  // so we query by status and filter client-side
  const constraints: QueryConstraint[] = [
    where('companyId', '==', companyId),
    where('status', 'in', statuses),
  ];
  
  if (options.limit) {
    constraints.push(firestoreLimit(options.limit * 2)); // Over-fetch for filtering
  }
  
  const q = query(collection(db, path), ...constraints);
  const snapshot = await getDocs(q);
  
  const orphanWells: WellForAssignment[] = [];
  
  for (const doc of snapshot.docs) {
    const data = doc.data();
    const well = migrateWellAssignmentFields(data);
    
    // Check if truly orphaned
    const isOrphan = (
      well.assignedPumperIds.length === 0 &&
      !well.primaryPumperId &&
      !well.assignedPumperId
    );
    
    if (isOrphan) {
      orphanWells.push(well);
      
      // Respect limit
      if (options.limit && orphanWells.length >= options.limit) {
        break;
      }
    }
  }
  
  if (orphanWells.length > 0) {
    console.warn(`⚠️ Found ${orphanWells.length} orphan wells`);
  }
  
  return orphanWells;
}

/**
 * Get wells assigned to a specific pumper.
 * Respects well visibility settings.
 * 
 * @param pumperId - Pumper ID
 * @param options - Query options
 * @returns Array of wells
 */
export async function getWellsForPumper(
  pumperId: string,
  options: {
    /** Include wells where pumper is backup */
    includeBackup?: boolean;
    /** Include wells where pumper is providing temporary coverage */
    includeCoverage?: boolean;
    /** Filter by status */
    status?: string[];
  } = {}
): Promise<WellForAssignment[]> {
  const companyId = getCompanyId();
  const path = FIRESTORE_PATHS.wells(companyId);
  
  // Query wells where pumper is in assignedPumperIds
  const constraints: QueryConstraint[] = [
    where('companyId', '==', companyId),
    where('assignedPumperIds', 'array-contains', pumperId),
  ];
  
  if (options.status && options.status.length > 0) {
    constraints.push(where('status', 'in', options.status));
  }
  
  const q = query(collection(db, path), ...constraints);
  const snapshot = await getDocs(q);
  
  const wells: WellForAssignment[] = [];
  
  for (const doc of snapshot.docs) {
    const data = doc.data();
    const well = migrateWellAssignmentFields(data);
    
    // Filter based on options
    const isPrimary = well.primaryPumperId === pumperId;
    const isBackup = well.backupPumperId === pumperId;
    const isCovering = well.assignedPumperIds.includes(pumperId) && !isPrimary && !isBackup;
    
    if (isPrimary) {
      wells.push(well);
    } else if (isBackup && options.includeBackup) {
      wells.push(well);
    } else if (isCovering && options.includeCoverage) {
      wells.push(well);
    }
  }
  
  return wells;
}

// ═══════════════════════════════════════════════════════════════════════════════
// TEMPORARY COVERAGE SERVICE (Tasks 49-55)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Input for creating a temporary coverage assignment
 */
export interface CreateTemporaryCoverageInput {
  /** Pumper providing coverage */
  coveringPumper: PumperForAssignment;
  /** Pumper being covered */
  originalPumper: PumperForAssignment;
  /** Wells to include in coverage */
  wells: Array<{ id: string; name: string }>;
  /** Reason for coverage */
  reason: CoverageReason;
  /** Additional notes (required if reason is 'other') */
  reasonNote?: string;
  /** Who is creating this coverage */
  performedBy: string;
  performedByName: string;
}

/**
 * Input for known end date coverage
 */
export interface FixedEndDateCoverageInput extends CreateTemporaryCoverageInput {
  /** Known end date (required for fixed coverage) */
  endDate: Date;
  /** Action when coverage ends */
  endAction: 'reassign' | 'unassign' | 'notify';
}

/**
 * Input for reminder-based coverage (unknown end date)
 */
export interface ReminderBasedCoverageInput extends CreateTemporaryCoverageInput {
  /** When to remind manager to check on coverage */
  reminderDate: Date;
  /** Action when coverage ends (defaults to 'notify' since we don't know when) */
  endAction?: 'notify';
}

/**
 * Result of a temporary coverage operation
 */
export interface TemporaryCoverageResult {
  success: boolean;
  /** ID of the TemporaryAssignmentDocument */
  coverageId?: string;
  /** Error message if failed */
  error?: string;
  /** Wells that failed to update */
  failedWells?: string[];
}

/**
 * Check if a pumper is already providing coverage for anyone.
 * Prevents nested coverage scenarios (pumper A covering B while also covering C).
 * 
 * Task 51: Nested coverage validation
 * 
 * @param pumperId - Pumper to check
 * @returns Active coverage if found, null otherwise
 */
export async function getActiveCoverageByPumper(
  pumperId: string
): Promise<TemporaryAssignmentDocument | null> {
  const companyId = getCompanyId();
  const path = FIRESTORE_PATHS.temporaryAssignments(companyId);
  
  const q = query(
    collection(db, path),
    where('coveringPumperId', '==', pumperId),
    where('status', 'in', ['active', 'extended']),
    firestoreLimit(1)
  );
  
  const snapshot = await getDocs(q);
  
  if (snapshot.empty) {
    return null;
  }
  
  const doc = snapshot.docs[0];
  return {
    id: doc.id,
    ...doc.data(),
  } as TemporaryAssignmentDocument;
}

/**
 * Check if a pumper is being covered by someone.
 * 
 * @param pumperId - Pumper to check
 * @returns Active coverage if found, null otherwise
 */
export async function getActiveCoverageForPumper(
  pumperId: string
): Promise<TemporaryAssignmentDocument | null> {
  const companyId = getCompanyId();
  const path = FIRESTORE_PATHS.temporaryAssignments(companyId);
  
  const q = query(
    collection(db, path),
    where('originalPumperId', '==', pumperId),
    where('status', 'in', ['active', 'extended']),
    firestoreLimit(1)
  );
  
  const snapshot = await getDocs(q);
  
  if (snapshot.empty) {
    return null;
  }
  
  const doc = snapshot.docs[0];
  return {
    id: doc.id,
    ...doc.data(),
  } as TemporaryAssignmentDocument;
}

/**
 * Validate coverage request before creation.
 * Prevents invalid configurations and nested coverage.
 * 
 * @returns null if valid, error message if invalid
 */
async function validateCoverageRequest(
  input: CreateTemporaryCoverageInput
): Promise<string | null> {
  // Can't cover yourself
  if (input.coveringPumper.id === input.originalPumper.id) {
    return 'Cannot assign coverage to the same pumper';
  }
  
  // Must have at least one well
  if (!input.wells || input.wells.length === 0) {
    return 'At least one well is required for coverage';
  }
  
  // Reason 'other' requires a note
  if (input.reason === 'other' && !input.reasonNote?.trim()) {
    return 'Reason note is required when reason is "other"';
  }
  
  // Check if covering pumper is already providing coverage (nested coverage)
  const existingCoverage = await getActiveCoverageByPumper(input.coveringPumper.id);
  if (existingCoverage) {
    return `${input.coveringPumper.name} is already providing coverage for ${existingCoverage.originalPumperName}. ` +
           `End that coverage first before assigning new coverage.`;
  }
  
  // Check if original pumper already has active coverage
  const existingCoverageFor = await getActiveCoverageForPumper(input.originalPumper.id);
  if (existingCoverageFor) {
    return `${input.originalPumper.name} is already being covered by ${existingCoverageFor.coveringPumperName}. ` +
           `End that coverage first or extend it.`;
  }
  
  return null; // Valid
}

/**
 * Create a temporary coverage assignment with a known end date.
 * 
 * Task 49: Fixed end date coverage flow
 * 
 * This creates:
 * 1. A TemporaryAssignmentDocument in the temporary_assignments collection
 * 2. Updates each well with embedded TemporaryAssignment data
 * 3. Creates history entries for each well
 * 4. Optionally sends notifications
 * 
 * @param input - Coverage configuration with fixed end date
 * @returns Result with coverage ID or error
 */
export async function createTemporaryAssignment(
  input: FixedEndDateCoverageInput
): Promise<TemporaryCoverageResult> {
  const companyId = getCompanyId();
  
  try {
    // Validate the request
    const validationError = await validateCoverageRequest(input);
    if (validationError) {
      return { success: false, error: validationError };
    }
    
    // Validate end date is in the future
    const now = new Date();
    if (input.endDate <= now) {
      return { success: false, error: 'End date must be in the future' };
    }
    
    // Generate coverage document ID
    const coverageId = `cov_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const path = FIRESTORE_PATHS.temporaryAssignments(companyId);
    
    // Build the master coverage document
    const coverageDoc: Omit<TemporaryAssignmentDocument, 'id' | 'createdAt' | 'companyId'> = {
      coveringPumperId: input.coveringPumper.id,
      coveringPumperName: input.coveringPumper.name,
      originalPumperId: input.originalPumper.id,
      originalPumperName: input.originalPumper.name,
      wellIds: input.wells.map(w => w.id),
      wellNames: input.wells.map(w => w.name),
      wellCount: input.wells.length,
      reason: input.reason,
      reasonNote: input.reasonNote,
      startDate: now,
      endDate: input.endDate,
      reminderDate: null,
      reminderSent: false,
      endAction: input.endAction,
      status: 'active',
      createdBy: input.performedBy,
      createdByName: input.performedByName,
    };
    
    // Create the master coverage document
    await guardedCreate(path, coverageId, coverageDoc, { userId: input.performedBy });
    
    // Update each well with embedded temporary assignment
    const failedWells: string[] = [];
    const wellPath = FIRESTORE_PATHS.wells(companyId);
    
    const embeddedAssignment: TemporaryAssignment = {
      active: true,
      coveringPumperId: input.coveringPumper.id,
      coveringPumperName: input.coveringPumper.name,
      originalPumperId: input.originalPumper.id,
      originalPumperName: input.originalPumper.name,
      reason: input.reason,
      reasonNote: input.reasonNote,
      startDate: now,
      endDate: input.endDate,
      reminderDate: null,
      endAction: input.endAction,
      createdBy: input.performedBy,
      createdByName: input.performedByName,
      createdAt: now,
    };
    
    for (const well of input.wells) {
      try {
        // Update well document
        await guardedUpdate(wellPath, well.id, {
          temporaryAssignment: embeddedAssignment,
          // Add covering pumper to assignedPumperIds if not already there
          ...syncAssignmentFields([input.coveringPumper.id]),
        }, { userId: input.performedBy });
        
        // Create history entry
        await createAssignmentHistoryEntry(well.id, well.name, 'temporary_started', {
          pumperIds: [input.coveringPumper.id],
          pumperNames: [input.coveringPumper.name],
          method: 'temporary',
          reason: `Covering for ${input.originalPumper.name}: ${input.reason}`,
          performedBy: input.performedBy,
          performedByName: input.performedByName,
          metadata: {
            temporaryAssignment: true,
            coveringFor: input.originalPumper.id,
            coveringForName: input.originalPumper.name,
            coverageReason: input.reason,
            coverageEndDate: input.endDate.toISOString(),
            coverageId,
          },
        });
      } catch (error) {
        console.error(`Failed to update well ${well.id}:`, error);
        failedWells.push(well.id);
      }
    }
    
    // Log result
    const successCount = input.wells.length - failedWells.length;
    console.log(`✅ Created temporary coverage ${coverageId}: ${successCount}/${input.wells.length} wells updated`);
    
    // Send coverage started notifications (Task 68)
    if (successCount > 0) {
      await notifyCoverageStarted(
        input.originalPumper.id,
        input.originalPumper.name,
        input.coveringPumper.id,
        input.coveringPumper.name,
        input.wells.filter(w => !failedWells.includes(w.id)),
        input.endDate,
        input.reason
      );
    }
    
    return {
      success: failedWells.length === 0,
      coverageId,
      failedWells: failedWells.length > 0 ? failedWells : undefined,
      error: failedWells.length > 0 
        ? `Coverage created but ${failedWells.length} wells failed to update` 
        : undefined,
    };
  } catch (error) {
    console.error('Failed to create temporary coverage:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error creating coverage',
    };
  }
}

/**
 * Create a reminder-based temporary coverage (unknown end date).
 * 
 * Task 50: Reminder-based coverage flow
 * 
 * Use this when the original pumper's return date is unknown (e.g., illness, injury).
 * A reminder will be sent when the reminderDate is reached to check status.
 * 
 * @param input - Coverage configuration with reminder date
 * @returns Result with coverage ID or error
 */
export async function createReminderBasedCoverage(
  input: ReminderBasedCoverageInput
): Promise<TemporaryCoverageResult> {
  const companyId = getCompanyId();
  
  try {
    // Validate the request
    const validationError = await validateCoverageRequest(input);
    if (validationError) {
      return { success: false, error: validationError };
    }
    
    // Validate reminder date is in the future
    const now = new Date();
    if (input.reminderDate <= now) {
      return { success: false, error: 'Reminder date must be in the future' };
    }
    
    // Generate coverage document ID
    const coverageId = `cov_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const path = FIRESTORE_PATHS.temporaryAssignments(companyId);
    
    // Build the master coverage document (reminder-based = null endDate)
    const coverageDoc: Omit<TemporaryAssignmentDocument, 'id' | 'createdAt' | 'companyId'> = {
      coveringPumperId: input.coveringPumper.id,
      coveringPumperName: input.coveringPumper.name,
      originalPumperId: input.originalPumper.id,
      originalPumperName: input.originalPumper.name,
      wellIds: input.wells.map(w => w.id),
      wellNames: input.wells.map(w => w.name),
      wellCount: input.wells.length,
      reason: input.reason,
      reasonNote: input.reasonNote,
      startDate: now,
      endDate: null, // Unknown end date
      reminderDate: input.reminderDate,
      reminderSent: false,
      endAction: 'notify', // Always notify for reminder-based
      status: 'active',
      createdBy: input.performedBy,
      createdByName: input.performedByName,
    };
    
    // Create the master coverage document
    await guardedCreate(path, coverageId, coverageDoc, { userId: input.performedBy });
    
    // Update each well with embedded temporary assignment
    const failedWells: string[] = [];
    const wellPath = FIRESTORE_PATHS.wells(companyId);
    
    const embeddedAssignment: TemporaryAssignment = {
      active: true,
      coveringPumperId: input.coveringPumper.id,
      coveringPumperName: input.coveringPumper.name,
      originalPumperId: input.originalPumper.id,
      originalPumperName: input.originalPumper.name,
      reason: input.reason,
      reasonNote: input.reasonNote,
      startDate: now,
      endDate: null,
      reminderDate: input.reminderDate,
      endAction: 'notify',
      createdBy: input.performedBy,
      createdByName: input.performedByName,
      createdAt: now,
    };
    
    for (const well of input.wells) {
      try {
        await guardedUpdate(wellPath, well.id, {
          temporaryAssignment: embeddedAssignment,
          ...syncAssignmentFields([input.coveringPumper.id]),
        }, { userId: input.performedBy });
        
        await createAssignmentHistoryEntry(well.id, well.name, 'temporary_started', {
          pumperIds: [input.coveringPumper.id],
          pumperNames: [input.coveringPumper.name],
          method: 'temporary',
          reason: `Covering for ${input.originalPumper.name}: ${input.reason} (open-ended)`,
          performedBy: input.performedBy,
          performedByName: input.performedByName,
          metadata: {
            temporaryAssignment: true,
            coveringFor: input.originalPumper.id,
            coveringForName: input.originalPumper.name,
            coverageReason: input.reason,
            reminderBased: true,
            reminderDate: input.reminderDate.toISOString(),
            coverageId,
          },
        });
      } catch (error) {
        console.error(`Failed to update well ${well.id}:`, error);
        failedWells.push(well.id);
      }
    }
    
    console.log(`✅ Created reminder-based coverage ${coverageId}`);
    
    return {
      success: failedWells.length === 0,
      coverageId,
      failedWells: failedWells.length > 0 ? failedWells : undefined,
    };
  } catch (error) {
    console.error('Failed to create reminder-based coverage:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * End a temporary coverage assignment.
 * 
 * Task 52: Handles early return, scheduled end, and manual termination
 * 
 * @param coverageId - ID of the coverage to end
 * @param options - End options
 * @returns Result indicating success/failure
 */
export async function endTemporaryAssignment(
  coverageId: string,
  options: {
    /** Why coverage is ending */
    endReason: CoverageEndReason;
    /** Who is ending it */
    performedBy: string;
    performedByName: string;
    /** Additional notes */
    notes?: string;
  }
): Promise<TemporaryCoverageResult> {
  const companyId = getCompanyId();
  
  try {
    // Fetch the coverage document
    const coveragePath = FIRESTORE_PATHS.temporaryAssignment(companyId, coverageId);
    const coverageRef = doc(db, coveragePath);
    const coverageSnap = await getDoc(coverageRef);
    
    if (!coverageSnap.exists()) {
      return { success: false, error: `Coverage ${coverageId} not found` };
    }
    
    const coverage = { id: coverageSnap.id, ...coverageSnap.data() } as TemporaryAssignmentDocument;
    
    // Check if already ended
    if (coverage.status === 'ended') {
      return { success: false, error: 'Coverage has already ended' };
    }
    
    const now = new Date();
    
    // Update the master coverage document
    await guardedUpdate(
      FIRESTORE_PATHS.temporaryAssignments(companyId),
      coverageId,
      {
        status: 'ended' as TemporaryAssignmentStatus,
        endedAt: now,
        endedBy: options.performedBy,
        endedByName: options.performedByName,
        endReason: options.endReason,
        updatedAt: serverTimestamp(),
      },
      { userId: options.performedBy }
    );
    
    // Clear temporary assignment from each well
    const failedWells: string[] = [];
    const wellPath = FIRESTORE_PATHS.wells(companyId);
    
    for (const wellId of coverage.wellIds) {
      try {
        // Determine what to do based on endAction
        let updateData: Record<string, unknown> = {
          temporaryAssignment: { active: false },
        };
        
        // Execute the end action
        switch (coverage.endAction) {
          case 'reassign':
            // Restore original pumper as primary
            updateData = {
              ...updateData,
              ...syncAssignmentFields([coverage.originalPumperId]),
              primaryPumperId: coverage.originalPumperId,
              primaryPumperName: coverage.originalPumperName,
            };
            break;
          case 'unassign':
            // Clear all assignments
            updateData = {
              ...updateData,
              ...createUnassignmentFields(),
            };
            break;
          case 'notify': {
            // Just clear temporary, leave current assignment
            // Remove covering pumper from assignedPumperIds
            const notifyWellRef = doc(db, wellPath, wellId);
            const notifyWellSnap = await getDoc(notifyWellRef);
            if (notifyWellSnap.exists()) {
              const wellData = notifyWellSnap.data();
              const currentIds = wellData.assignedPumperIds || [];
              const newIds = currentIds.filter((id: string) => id !== coverage.coveringPumperId);
              // Restore original if they were removed
              if (!newIds.includes(coverage.originalPumperId)) {
                newIds.unshift(coverage.originalPumperId);
              }
              updateData = {
                ...updateData,
                ...syncAssignmentFields(newIds),
                primaryPumperId: coverage.originalPumperId,
                primaryPumperName: coverage.originalPumperName,
              };
            }
            break;
          }
        }
        
        await guardedUpdate(wellPath, wellId, updateData, { userId: options.performedBy });
        
        // Get well name for history
        const historyWellRef = doc(db, wellPath, wellId);
        const historyWellSnap = await getDoc(historyWellRef);
        const wellName = historyWellSnap.exists() ? historyWellSnap.data().name || wellId : wellId;
        
        // Create history entry
        await createAssignmentHistoryEntry(wellId, wellName, 'temporary_ended', {
          pumperIds: [coverage.coveringPumperId],
          pumperNames: [coverage.coveringPumperName],
          method: 'coverage_end',
          reason: `Coverage ended: ${options.endReason}`,
          performedBy: options.performedBy,
          performedByName: options.performedByName,
          metadata: {
            endReason: options.endReason,
            originalPumperId: coverage.originalPumperId,
            originalPumperName: coverage.originalPumperName,
            coverageId,
            notes: options.notes,
          },
        });
      } catch (error) {
        console.error(`Failed to clear coverage from well ${wellId}:`, error);
        failedWells.push(wellId);
      }
    }
    
    console.log(`✅ Ended temporary coverage ${coverageId} (${options.endReason})`);
    
    // Send coverage ended notifications (Task 70)
    if (coverage.wellIds.length > 0) {
      const wellInfos = coverage.wellIds.map((id, i) => ({
        id,
        name: coverage.wellNames?.[i] || id
      }));
      await notifyCoverageEnded(
        coverage.originalPumperId,
        coverage.originalPumperName,
        coverage.coveringPumperId,
        coverage.coveringPumperName,
        wellInfos
      );
    }
    
    return {
      success: failedWells.length === 0,
      coverageId,
      failedWells: failedWells.length > 0 ? failedWells : undefined,
    };
  } catch (error) {
    console.error('Failed to end temporary coverage:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Extend a temporary coverage assignment.
 * 
 * Task 53: Update end date or push reminder date forward
 * 
 * @param coverageId - ID of the coverage to extend
 * @param options - Extension options
 * @returns Result indicating success/failure
 */
export async function extendTemporaryAssignment(
  coverageId: string,
  options: {
    /** New end date (for fixed-end coverage) */
    newEndDate?: Date;
    /** New reminder date (for reminder-based coverage) */
    newReminderDate?: Date;
    /** Extension reason/notes */
    reason?: string;
    /** Who is extending */
    performedBy: string;
    performedByName: string;
  }
): Promise<TemporaryCoverageResult> {
  const companyId = getCompanyId();
  
  try {
    // Fetch current coverage
    const coveragePath = FIRESTORE_PATHS.temporaryAssignment(companyId, coverageId);
    const coverageRef = doc(db, coveragePath);
    const coverageSnap = await getDoc(coverageRef);
    
    if (!coverageSnap.exists()) {
      return { success: false, error: `Coverage ${coverageId} not found` };
    }
    
    const coverage = { id: coverageSnap.id, ...coverageSnap.data() } as TemporaryAssignmentDocument;
    
    // Check if coverage is active
    if (coverage.status === 'ended') {
      return { success: false, error: 'Cannot extend ended coverage' };
    }
    
    // Validate extension based on coverage type
    const isReminderBased = coverage.endDate === null;
    
    if (isReminderBased && !options.newReminderDate) {
      return { success: false, error: 'newReminderDate required for reminder-based coverage' };
    }
    
    if (!isReminderBased && !options.newEndDate) {
      return { success: false, error: 'newEndDate required for fixed-end coverage' };
    }
    
    const now = new Date();
    
    // Validate new date is in the future
    const newDate = isReminderBased ? options.newReminderDate! : options.newEndDate!;
    if (newDate <= now) {
      return { success: false, error: 'New date must be in the future' };
    }
    
    // Update coverage document
    const updateData: Record<string, unknown> = {
      status: 'extended' as TemporaryAssignmentStatus,
      updatedAt: serverTimestamp(),
    };
    
    if (isReminderBased) {
      updateData.reminderDate = options.newReminderDate;
      updateData.reminderSent = false; // Reset reminder flag
    } else {
      updateData.endDate = options.newEndDate;
    }
    
    await guardedUpdate(
      FIRESTORE_PATHS.temporaryAssignments(companyId),
      coverageId,
      updateData,
      { userId: options.performedBy }
    );
    
    // Update embedded assignment in each well
    const failedWells: string[] = [];
    const wellPath = FIRESTORE_PATHS.wells(companyId);
    
    for (const wellId of coverage.wellIds) {
      try {
        const wellUpdateData: Record<string, unknown> = {
          'temporaryAssignment.endDate': isReminderBased ? null : options.newEndDate,
          'temporaryAssignment.reminderDate': isReminderBased ? options.newReminderDate : null,
        };
        
        await guardedUpdate(wellPath, wellId, wellUpdateData, { userId: options.performedBy });
        
        // Create history entry
        const wellRef = doc(db, wellPath, wellId);
        const wellSnap = await getDoc(wellRef);
        const wellName = wellSnap.exists() ? wellSnap.data().name || wellId : wellId;
        
        await createAssignmentHistoryEntry(wellId, wellName, 'temporary_extended', {
          pumperIds: [coverage.coveringPumperId],
          pumperNames: [coverage.coveringPumperName],
          method: 'temporary',
          reason: options.reason || 'Coverage extended',
          performedBy: options.performedBy,
          performedByName: options.performedByName,
          metadata: {
            previousEndDate: coverage.endDate ? (coverage.endDate as Date).toISOString?.() : null,
            previousReminderDate: coverage.reminderDate ? (coverage.reminderDate as Date).toISOString?.() : null,
            newEndDate: options.newEndDate?.toISOString(),
            newReminderDate: options.newReminderDate?.toISOString(),
            coverageId,
          },
        });
      } catch (error) {
        console.error(`Failed to extend coverage on well ${wellId}:`, error);
        failedWells.push(wellId);
      }
    }
    
    console.log(`✅ Extended coverage ${coverageId}`);
    
    return {
      success: failedWells.length === 0,
      coverageId,
      failedWells: failedWells.length > 0 ? failedWells : undefined,
    };
  } catch (error) {
    console.error('Failed to extend coverage:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Convert temporary coverage to permanent assignment.
 * 
 * Task 54: Make covering pumper the new permanent primary
 * 
 * Use case: Original pumper quit, retired, or permanently transferred.
 * The covering pumper becomes the new primary and coverage ends.
 * 
 * @param coverageId - ID of the coverage to make permanent
 * @param options - Conversion options
 * @returns Result indicating success/failure
 */
export async function makeCoveragePermanent(
  coverageId: string,
  options: {
    /** Reason for making permanent */
    reason?: string;
    /** Who is converting */
    performedBy: string;
    performedByName: string;
  }
): Promise<TemporaryCoverageResult> {
  const companyId = getCompanyId();
  
  try {
    // Fetch current coverage
    const coveragePath = FIRESTORE_PATHS.temporaryAssignment(companyId, coverageId);
    const coverageRef = doc(db, coveragePath);
    const coverageSnap = await getDoc(coverageRef);
    
    if (!coverageSnap.exists()) {
      return { success: false, error: `Coverage ${coverageId} not found` };
    }
    
    const coverage = { id: coverageSnap.id, ...coverageSnap.data() } as TemporaryAssignmentDocument;
    
    // Check if coverage is active
    if (coverage.status === 'ended') {
      return { success: false, error: 'Cannot convert ended coverage to permanent' };
    }
    
    const now = new Date();
    
    // End the coverage document with 'made_permanent' reason
    await guardedUpdate(
      FIRESTORE_PATHS.temporaryAssignments(companyId),
      coverageId,
      {
        status: 'ended' as TemporaryAssignmentStatus,
        endedAt: now,
        endedBy: options.performedBy,
        endedByName: options.performedByName,
        endReason: 'made_permanent' as CoverageEndReason,
        updatedAt: serverTimestamp(),
      },
      { userId: options.performedBy }
    );
    
    // Convert each well's assignment
    const failedWells: string[] = [];
    const wellPath = FIRESTORE_PATHS.wells(companyId);
    
    for (const wellId of coverage.wellIds) {
      try {
        // Make covering pumper the permanent primary
        await guardedUpdate(wellPath, wellId, {
          // Clear temporary assignment
          temporaryAssignment: { active: false },
          // Set covering pumper as permanent primary
          ...syncAssignmentFields([coverage.coveringPumperId]),
          primaryPumperId: coverage.coveringPumperId,
          primaryPumperName: coverage.coveringPumperName,
          // Clear backup since role has changed
          backupPumperId: null,
          backupPumperName: null,
          backupActive: false,
        }, { userId: options.performedBy });
        
        // Get well name for history
        const wellRef = doc(db, wellPath, wellId);
        const wellSnap = await getDoc(wellRef);
        const wellName = wellSnap.exists() ? wellSnap.data().name || wellId : wellId;
        
        // Create history entry
        await createAssignmentHistoryEntry(wellId, wellName, 'assigned', {
          pumperIds: [coverage.coveringPumperId],
          pumperNames: [coverage.coveringPumperName],
          method: 'coverage_end',
          reason: options.reason || `Temporary coverage made permanent (was covering for ${coverage.originalPumperName})`,
          performedBy: options.performedBy,
          performedByName: options.performedByName,
          metadata: {
            madePermanent: true,
            previousTemporaryAssignment: true,
            previousOriginalPumperId: coverage.originalPumperId,
            previousOriginalPumperName: coverage.originalPumperName,
            coverageId,
          },
        });
      } catch (error) {
        console.error(`Failed to make well ${wellId} permanent:`, error);
        failedWells.push(wellId);
      }
    }
    
    console.log(`✅ Made coverage ${coverageId} permanent — ${coverage.coveringPumperName} is now primary`);
    
    // Notify pumpers of permanent assignment change
    const wellInfos = coverage.wellIds.map((id, i) => ({
      id,
      name: coverage.wellNames?.[i] || id
    }));
    
    // Notify original pumper they're no longer assigned (use unassignment)
    if (coverage.originalPumperId) {
      const settings = await getWellVisibilitySettings();
      if (settings.notifyOnUnassign) {
        for (const well of wellInfos) {
          await notifyPumperOfUnassignment(
            coverage.originalPumperId,
            coverage.originalPumperName,
            well,
            {
              reason: `Coverage by ${coverage.coveringPumperName} made permanent`,
              performedBy: options.performedBy,
              performedByName: options.performedByName,
            }
          );
        }
      }
    }
    
    // Notify covering pumper they're now permanent (use bulk assignment)
    await notifyPumperOfBulkAssignment(
      coverage.coveringPumperId,
      coverage.coveringPumperName,
      wellInfos.map(w => ({ wellId: w.id, wellName: w.name, success: true })),
      {
        reason: options.reason || 'Temporary coverage made permanent',
        performedBy: options.performedBy,
        performedByName: options.performedByName,
        assignmentType: 'primary',
      }
    );
    
    return {
      success: failedWells.length === 0,
      coverageId,
      failedWells: failedWells.length > 0 ? failedWells : undefined,
    };
  } catch (error) {
    console.error('Failed to make coverage permanent:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Get all active coverage arrangements for a company.
 * 
 * Task 55: Query active coverages
 * 
 * @param options - Query options
 * @returns Array of active coverage documents
 */
export async function getActiveCoverages(
  options: {
    /** Filter by covering pumper */
    coveringPumperId?: string;
    /** Filter by original pumper being covered */
    originalPumperId?: string;
    /** Include extended status as active */
    includeExtended?: boolean;
    /** Limit results */
    limit?: number;
  } = {}
): Promise<TemporaryAssignmentDocument[]> {
  const companyId = getCompanyId();
  const path = FIRESTORE_PATHS.temporaryAssignments(companyId);
  
  const statuses: TemporaryAssignmentStatus[] = ['active'];
  if (options.includeExtended !== false) {
    statuses.push('extended');
  }
  
  const constraints: QueryConstraint[] = [
    where('companyId', '==', companyId),
    where('status', 'in', statuses),
  ];
  
  if (options.coveringPumperId) {
    constraints.push(where('coveringPumperId', '==', options.coveringPumperId));
  }
  
  if (options.originalPumperId) {
    constraints.push(where('originalPumperId', '==', options.originalPumperId));
  }
  
  if (options.limit) {
    constraints.push(firestoreLimit(options.limit));
  }
  
  const q = query(collection(db, path), ...constraints);
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    // Convert Firestore timestamps to dates
    startDate: doc.data().startDate?.toDate?.() ?? doc.data().startDate,
    endDate: doc.data().endDate?.toDate?.() ?? doc.data().endDate,
    reminderDate: doc.data().reminderDate?.toDate?.() ?? doc.data().reminderDate,
    createdAt: doc.data().createdAt?.toDate?.() ?? doc.data().createdAt,
    endedAt: doc.data().endedAt?.toDate?.() ?? doc.data().endedAt,
    updatedAt: doc.data().updatedAt?.toDate?.() ?? doc.data().updatedAt,
  })) as TemporaryAssignmentDocument[];
}

/**
 * Get a single coverage document by ID.
 * 
 * @param coverageId - Coverage document ID
 * @returns Coverage document or null if not found
 */
export async function getCoverageById(
  coverageId: string
): Promise<TemporaryAssignmentDocument | null> {
  const companyId = getCompanyId();
  const coveragePath = FIRESTORE_PATHS.temporaryAssignment(companyId, coverageId);
  
  const coverageRef = doc(db, coveragePath);
  const coverageSnap = await getDoc(coverageRef);
  
  if (!coverageSnap.exists()) {
    return null;
  }
  
  const data = coverageSnap.data();
  return {
    id: coverageSnap.id,
    ...data,
    startDate: data.startDate?.toDate?.() ?? data.startDate,
    endDate: data.endDate?.toDate?.() ?? data.endDate,
    reminderDate: data.reminderDate?.toDate?.() ?? data.reminderDate,
    createdAt: data.createdAt?.toDate?.() ?? data.createdAt,
    endedAt: data.endedAt?.toDate?.() ?? data.endedAt,
    updatedAt: data.updatedAt?.toDate?.() ?? data.updatedAt,
  } as TemporaryAssignmentDocument;
}

/**
 * Get coverages that have reminders due (for scheduled jobs).
 * 
 * Bonus utility for Task 50's reminder system.
 * Called by cloud function or scheduled task.
 * 
 * @returns Coverages with overdue reminders
 */
export async function getCoveragesWithDueReminders(): Promise<TemporaryAssignmentDocument[]> {
  const companyId = getCompanyId();
  const path = FIRESTORE_PATHS.temporaryAssignments(companyId);
  
  const now = new Date();
  
  const q = query(
    collection(db, path),
    where('status', 'in', ['active', 'extended']),
    where('reminderDate', '!=', null),
    where('reminderSent', '==', false)
  );
  
  const snapshot = await getDocs(q);
  
  // Filter client-side for reminders that are due
  const dueCoverages: TemporaryAssignmentDocument[] = [];
  
  for (const docSnap of snapshot.docs) {
    const data = docSnap.data();
    const reminderDate = data.reminderDate?.toDate?.() ?? new Date(data.reminderDate.seconds * 1000);
    
    if (reminderDate <= now) {
      dueCoverages.push({
        id: docSnap.id,
        ...data,
        reminderDate,
        startDate: data.startDate?.toDate?.() ?? data.startDate,
        createdAt: data.createdAt?.toDate?.() ?? data.createdAt,
      } as TemporaryAssignmentDocument);
    }
  }
  
  return dueCoverages;
}

/**
 * Mark a reminder as sent (called after notification is delivered).
 * 
 * @param coverageId - Coverage to update
 * @param performedBy - Who triggered the reminder
 */
export async function markReminderSent(
  coverageId: string,
  performedBy: string
): Promise<void> {
  const companyId = getCompanyId();
  
  await guardedUpdate(
    FIRESTORE_PATHS.temporaryAssignments(companyId),
    coverageId,
    {
      reminderSent: true,
      updatedAt: serverTimestamp(),
    },
    { userId: performedBy }
  );
  
  console.log(`📬 Marked reminder sent for coverage ${coverageId}`);
}

/**
 * Get coverages ending soon (for proactive notifications).
 * 
 * @param withinDays - Days until end (default: 1)
 * @returns Coverages ending within the specified window
 */
export async function getCoveragesEndingSoon(
  withinDays: number = 1
): Promise<TemporaryAssignmentDocument[]> {
  const companyId = getCompanyId();
  const path = FIRESTORE_PATHS.temporaryAssignments(companyId);
  
  const now = new Date();
  const futureDate = new Date(now.getTime() + (withinDays * 24 * 60 * 60 * 1000));
  
  // Query active coverages with fixed end dates
  const q = query(
    collection(db, path),
    where('status', 'in', ['active', 'extended']),
    where('endDate', '!=', null),
    where('endDate', '<=', futureDate)
  );
  
  const snapshot = await getDocs(q);
  
  // Filter to only future end dates (not past)
  const endingSoon: TemporaryAssignmentDocument[] = [];
  
  for (const docSnap of snapshot.docs) {
    const data = docSnap.data();
    const endDate = data.endDate?.toDate?.() ?? new Date(data.endDate.seconds * 1000);
    
    if (endDate > now) {
      endingSoon.push({
        id: docSnap.id,
        ...data,
        endDate,
        startDate: data.startDate?.toDate?.() ?? data.startDate,
        createdAt: data.createdAt?.toDate?.() ?? data.createdAt,
      } as TemporaryAssignmentDocument);
    }
  }
  
  return endingSoon;
}

// ═══════════════════════════════════════════════════════════════════════════════
// BACKUP SYSTEM SERVICE (Tasks 56-60)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Result of a backup activation/deactivation operation
 */
export interface BackupOperationResult {
  success: boolean;
  wellId: string;
  wellName?: string;
  error?: string;
}

/**
 * Result of a bulk backup operation
 */
export interface BulkBackupOperationResult {
  success: boolean;
  totalWells: number;
  successCount: number;
  failureCount: number;
  results: BackupOperationResult[];
  /** Wells without backup configured */
  wellsWithoutBackup: Array<{ id: string; name: string }>;
  /** Wells that had errors */
  failures: BackupOperationResult[];
}

/**
 * Options for backup operations
 */
export interface BackupActivationOptions {
  /** Reason for activation (shown in notifications/history) */
  reason?: string;
  /** Who is performing the action */
  performedBy: string;
  performedByName: string;
  /** Skip sending notifications */
  skipNotification?: boolean;
}

/**
 * Well data with backup info for backup operations
 */
export interface WellWithBackupInfo {
  id: string;
  name: string;
  companyId: string;
  /** Current primary pumper */
  primaryPumperId: string | null;
  primaryPumperName: string | null;
  /** Configured backup pumper */
  backupPumperId: string | null;
  backupPumperName: string | null;
  /** Whether backup is currently active */
  backupActive: boolean;
  /** Current assignment state */
  assignedPumperIds: string[];
  status: string;
}

/**
 * Get wells for a pumper with backup information.
 * Used to display which wells have backups configured before activation.
 * 
 * @param pumperId - Primary pumper ID
 * @returns Wells with backup status
 */
export async function getWellsWithBackupInfo(
  pumperId: string
): Promise<WellWithBackupInfo[]> {
  const companyId = getCompanyId();
  const path = FIRESTORE_PATHS.wells(companyId);
  
  const q = query(
    collection(db, path),
    where('companyId', '==', companyId),
    where('primaryPumperId', '==', pumperId),
    where('status', 'in', ['active', 'inactive', 'shut_in'])
  );
  
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(docSnap => {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      name: data.name || docSnap.id,
      companyId: data.companyId,
      primaryPumperId: data.primaryPumperId || null,
      primaryPumperName: data.primaryPumperName || null,
      backupPumperId: data.backupPumperId || null,
      backupPumperName: data.backupPumperName || null,
      backupActive: data.backupActive || false,
      assignedPumperIds: data.assignedPumperIds || [],
      status: data.status,
    };
  });
}

/**
 * Check if a well has a backup configured.
 * 
 * @param wellId - Well to check
 * @returns Whether backup is configured
 */
export async function hasBackupConfigured(wellId: string): Promise<boolean> {
  const companyId = getCompanyId();
  const path = FIRESTORE_PATHS.wells(companyId);
  
  const wellRef = doc(db, path, wellId);
  const wellSnap = await getDoc(wellRef);
  
  if (!wellSnap.exists()) return false;
  
  const data = wellSnap.data();
  return !!data.backupPumperId;
}

/**
 * Activate backup pumper for a single well.
 * 
 * Task 56: Single well backup activation
 * 
 * This:
 * 1. Validates backup is configured
 * 2. Sets backupActive = true
 * 3. Adds backup pumper to assignedPumperIds (if not already)
 * 4. Creates history entry
 * 5. Optionally sends notification
 * 
 * The backup pumper becomes the active responder while primary is out.
 * Primary pumper is NOT removed from assignments — they just aren't checking wells.
 * 
 * @param wellId - Well to activate backup for
 * @param options - Activation options
 * @returns Operation result
 */
export async function activateBackupForWell(
  wellId: string,
  options: BackupActivationOptions
): Promise<BackupOperationResult> {
  const companyId = getCompanyId();
  const path = FIRESTORE_PATHS.wells(companyId);
  
  try {
    // Get well data
    const wellRef = doc(db, path, wellId);
    const wellSnap = await getDoc(wellRef);
    
    if (!wellSnap.exists()) {
      return {
        success: false,
        wellId,
        error: `Well ${wellId} not found`,
      };
    }
    
    const wellData = wellSnap.data();
    const wellName = wellData.name || wellId;
    
    // Check if backup is configured
    if (!wellData.backupPumperId) {
      return {
        success: false,
        wellId,
        wellName,
        error: 'No backup pumper configured for this well',
      };
    }
    
    // Check if already active
    if (wellData.backupActive) {
      return {
        success: false,
        wellId,
        wellName,
        error: 'Backup is already active for this well',
      };
    }
    
    // Build update - add backup to assignedPumperIds if not present
    const currentIds = wellData.assignedPumperIds || [];
    const newIds = currentIds.includes(wellData.backupPumperId)
      ? currentIds
      : [...currentIds, wellData.backupPumperId];
    
    await guardedUpdate(path, wellId, {
      backupActive: true,
      assignedPumperIds: newIds,
      // Sync legacy field
      assignedPumperId: newIds[0] || null,
    }, { userId: options.performedBy });
    
    // Create history entry
    await createAssignmentHistoryEntry(wellId, wellName, 'backup_activated', {
      pumperIds: [wellData.backupPumperId],
      pumperNames: [wellData.backupPumperName || 'Unknown'],
      method: 'backup',
      reason: options.reason || 'Backup activated',
      performedBy: options.performedBy,
      performedByName: options.performedByName,
      metadata: {
        primaryPumperId: wellData.primaryPumperId,
        primaryPumperName: wellData.primaryPumperName,
        activationType: 'single',
      },
    });
    
    console.log(`🛡️ Activated backup for well ${wellName}`);
    
    // Send backup activation notification (Task 72)
    if (!options.skipNotification && wellData.backupPumperId) {
      await notifyBackupActivated(
        wellData.backupPumperId,
        wellData.backupPumperName || 'Backup Pumper',
        wellData.primaryPumperId || '',
        wellData.primaryPumperName || 'Primary Pumper',
        [{ id: wellId, name: wellName }],
        options.reason
      );
    }
    
    return {
      success: true,
      wellId,
      wellName,
    };
  } catch (error) {
    console.error(`Failed to activate backup for well ${wellId}:`, error);
    return {
      success: false,
      wellId,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Activate backups for ALL wells assigned to a pumper.
 * 
 * Task 57: Bulk backup activation
 * 
 * This is the "John is out sick, activate all his backups" operation.
 * Returns list of wells without backups configured for manual handling.
 * 
 * @param pumperId - Primary pumper whose backups to activate
 * @param options - Activation options
 * @returns Bulk operation result with wells needing manual attention
 */
export async function activateBackupsForPumper(
  pumperId: string,
  options: BackupActivationOptions
): Promise<BulkBackupOperationResult> {
  try {
    // Get all wells for this pumper
    const wells = await getWellsWithBackupInfo(pumperId);
    
    if (wells.length === 0) {
      return {
        success: true,
        totalWells: 0,
        successCount: 0,
        failureCount: 0,
        results: [],
        wellsWithoutBackup: [],
        failures: [],
      };
    }
    
    // Separate wells with and without backup
    const wellsWithBackup = wells.filter(w => w.backupPumperId);
    const wellsWithoutBackup = wells.filter(w => !w.backupPumperId).map(w => ({
      id: w.id,
      name: w.name,
    }));
    
    // Activate each well with backup
    const results: BackupOperationResult[] = [];
    const failures: BackupOperationResult[] = [];
    
    for (const well of wellsWithBackup) {
      // Skip if already active
      if (well.backupActive) {
        results.push({
          success: true,
          wellId: well.id,
          wellName: well.name,
        });
        continue;
      }
      
      const result = await activateBackupForWell(well.id, {
        ...options,
        reason: options.reason || `Bulk activation for ${options.performedByName}`,
        skipNotification: true, // We'll send one consolidated notification
      });
      
      results.push(result);
      if (!result.success) {
        failures.push(result);
      }
    }
    
    const successCount = results.filter(r => r.success).length;
    const failureCount = failures.length;
    
    console.log(`🛡️ Bulk backup activation: ${successCount}/${wellsWithBackup.length} succeeded, ${wellsWithoutBackup.length} wells have no backup`);
    
    // Log warning for wells without backup
    if (wellsWithoutBackup.length > 0) {
      console.warn(`⚠️ ${wellsWithoutBackup.length} wells have no backup configured:`, 
        wellsWithoutBackup.map(w => w.name).join(', '));
    }
    
    // Send consolidated backup activation notification (Task 72)
    if (!options.skipNotification && successCount > 0) {
      // Group successful wells by backup pumper for notification
      const successfulWells = results.filter(r => r.success);
      const backupPumpersNotified = new Set<string>();
      
      for (const wellResult of successfulWells) {
        const wellInfo = wellsWithBackup.find(w => w.id === wellResult.wellId);
        if (wellInfo && wellInfo.backupPumperId && !backupPumpersNotified.has(wellInfo.backupPumperId)) {
          // Get all wells for this backup pumper
          const wellsForBackup = successfulWells.filter(sr => {
            const wi = wellsWithBackup.find(w => w.id === sr.wellId);
            return wi && wi.backupPumperId === wellInfo.backupPumperId;
          });
          
          await notifyBackupActivated(
            wellInfo.backupPumperId,
            wellInfo.backupPumperName || 'Backup Pumper',
            pumperId,
            options.performedByName || 'Primary Pumper',
            wellsForBackup.map(wr => ({
              id: wr.wellId,
              name: wr.wellName || wr.wellId
            })),
            options.reason
          );
          backupPumpersNotified.add(wellInfo.backupPumperId);
        }
      }
    }
    
    return {
      success: failureCount === 0 && wellsWithoutBackup.length === 0,
      totalWells: wells.length,
      successCount,
      failureCount,
      results,
      wellsWithoutBackup,
      failures,
    };
  } catch (error) {
    console.error('Failed bulk backup activation:', error);
    return {
      success: false,
      totalWells: 0,
      successCount: 0,
      failureCount: 1,
      results: [],
      wellsWithoutBackup: [],
      failures: [{
        success: false,
        wellId: 'bulk',
        error: error instanceof Error ? error.message : 'Unknown error',
      }],
    };
  }
}

/**
 * Deactivate backup pumper for a single well.
 * 
 * Task 58: Single well backup deactivation
 * 
 * This:
 * 1. Sets backupActive = false
 * 2. Optionally removes backup from assignedPumperIds
 * 3. Creates history entry
 * 4. Optionally sends notification
 * 
 * @param wellId - Well to deactivate backup for
 * @param options - Deactivation options
 * @returns Operation result
 */
export async function deactivateBackupForWell(
  wellId: string,
  options: BackupActivationOptions & {
    /** Remove backup from assignedPumperIds (default: true) */
    removeFromAssigned?: boolean;
  }
): Promise<BackupOperationResult> {
  const companyId = getCompanyId();
  const path = FIRESTORE_PATHS.wells(companyId);
  
  const removeFromAssigned = options.removeFromAssigned !== false;
  
  try {
    // Get well data
    const wellRef = doc(db, path, wellId);
    const wellSnap = await getDoc(wellRef);
    
    if (!wellSnap.exists()) {
      return {
        success: false,
        wellId,
        error: `Well ${wellId} not found`,
      };
    }
    
    const wellData = wellSnap.data();
    const wellName = wellData.name || wellId;
    
    // Check if backup is active
    if (!wellData.backupActive) {
      return {
        success: false,
        wellId,
        wellName,
        error: 'Backup is not currently active for this well',
      };
    }
    
    // Build update
    let updateData: Record<string, unknown> = {
      backupActive: false,
    };
    
    // Optionally remove backup from assignedPumperIds
    if (removeFromAssigned && wellData.backupPumperId) {
      const currentIds = wellData.assignedPumperIds || [];
      const newIds = currentIds.filter((id: string) => id !== wellData.backupPumperId);
      updateData = {
        ...updateData,
        ...syncAssignmentFields(newIds),
      };
    }
    
    await guardedUpdate(path, wellId, updateData, { userId: options.performedBy });
    
    // Create history entry
    await createAssignmentHistoryEntry(wellId, wellName, 'backup_deactivated', {
      pumperIds: wellData.backupPumperId ? [wellData.backupPumperId] : [],
      pumperNames: wellData.backupPumperName ? [wellData.backupPumperName] : [],
      method: 'backup',
      reason: options.reason || 'Backup deactivated',
      performedBy: options.performedBy,
      performedByName: options.performedByName,
      metadata: {
        primaryPumperId: wellData.primaryPumperId,
        primaryPumperName: wellData.primaryPumperName,
        deactivationType: 'single',
      },
    });
    
    console.log(`🛡️ Deactivated backup for well ${wellName}`);
    
    // Send backup deactivation notification (Task 73)
    if (!options.skipNotification && wellData.backupPumperId) {
      await notifyBackupDeactivated(
        wellData.backupPumperId,
        wellData.backupPumperName || 'Backup Pumper',
        wellData.primaryPumperId || '',
        wellData.primaryPumperName || 'Primary Pumper',
        [{ id: wellId, name: wellName }],
        options.reason
      );
    }
    
    return {
      success: true,
      wellId,
      wellName,
    };
  } catch (error) {
    console.error(`Failed to deactivate backup for well ${wellId}:`, error);
    return {
      success: false,
      wellId,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Deactivate backups for ALL wells assigned to a pumper.
 * 
 * Task 59: Bulk backup deactivation
 * 
 * This is the "John is back, deactivate all his backups" operation.
 * Restores normal operations with primary pumper handling their wells.
 * 
 * @param pumperId - Primary pumper whose backups to deactivate
 * @param options - Deactivation options
 * @returns Bulk operation result
 */
export async function deactivateAllBackupsForPumper(
  pumperId: string,
  options: BackupActivationOptions & {
    /** Remove backups from assignedPumperIds (default: true) */
    removeFromAssigned?: boolean;
  }
): Promise<BulkBackupOperationResult> {
  try {
    // Get all wells for this pumper
    const wells = await getWellsWithBackupInfo(pumperId);
    
    // Filter to only wells with backup currently active
    const activeBackupWells = wells.filter(w => w.backupActive);
    
    if (activeBackupWells.length === 0) {
      return {
        success: true,
        totalWells: wells.length,
        successCount: 0,
        failureCount: 0,
        results: [],
        wellsWithoutBackup: [],
        failures: [],
      };
    }
    
    // Deactivate each well
    const results: BackupOperationResult[] = [];
    const failures: BackupOperationResult[] = [];
    
    for (const well of activeBackupWells) {
      const result = await deactivateBackupForWell(well.id, {
        ...options,
        reason: options.reason || `Bulk deactivation for ${options.performedByName}`,
        skipNotification: true,
      });
      
      results.push(result);
      if (!result.success) {
        failures.push(result);
      }
    }
    
    const successCount = results.filter(r => r.success).length;
    const failureCount = failures.length;
    
    console.log(`🛡️ Bulk backup deactivation: ${successCount}/${activeBackupWells.length} succeeded`);
    
    // Send consolidated backup deactivation notification (Task 73)
    if (!options.skipNotification && successCount > 0) {
      // Group successful wells by backup pumper for notification
      const successfulWells = results.filter(r => r.success);
      const backupPumpersNotified = new Set<string>();
      
      for (const wellResult of successfulWells) {
        const wellInfo = activeBackupWells.find(w => w.id === wellResult.wellId);
        if (wellInfo && wellInfo.backupPumperId && !backupPumpersNotified.has(wellInfo.backupPumperId)) {
          // Get all wells for this backup pumper
          const wellsForBackup = successfulWells.filter(sr => {
            const wi = activeBackupWells.find(w => w.id === sr.wellId);
            return wi && wi.backupPumperId === wellInfo.backupPumperId;
          });
          
          await notifyBackupDeactivated(
            wellInfo.backupPumperId,
            wellInfo.backupPumperName || 'Backup Pumper',
            pumperId,
            options.performedByName || 'Primary Pumper',
            wellsForBackup.map(wr => ({
              id: wr.wellId,
              name: wr.wellName || wr.wellId
            })),
            options.reason
          );
          backupPumpersNotified.add(wellInfo.backupPumperId);
        }
      }
    }
    
    return {
      success: failureCount === 0,
      totalWells: wells.length,
      successCount,
      failureCount,
      results,
      wellsWithoutBackup: [], // Not relevant for deactivation
      failures,
    };
  } catch (error) {
    console.error('Failed bulk backup deactivation:', error);
    return {
      success: false,
      totalWells: 0,
      successCount: 0,
      failureCount: 1,
      results: [],
      wellsWithoutBackup: [],
      failures: [{
        success: false,
        wellId: 'bulk',
        error: error instanceof Error ? error.message : 'Unknown error',
      }],
    };
  }
}

/**
 * Get wells for a pumper that DON'T have backups configured.
 * 
 * Task 60: Wells without backup for manual handling
 * 
 * Returns wells that need manual assignment when primary is out.
 * Critical for managers to know before approving time off.
 * 
 * @param pumperId - Primary pumper ID
 * @returns Wells without backup configured
 */
export async function getWellsWithoutBackup(
  pumperId: string
): Promise<Array<{ id: string; name: string; status: string }>> {
  const wells = await getWellsWithBackupInfo(pumperId);
  
  return wells
    .filter(w => !w.backupPumperId)
    .map(w => ({
      id: w.id,
      name: w.name,
      status: w.status,
    }));
}

/**
 * Get backup status summary for a pumper.
 * 
 * Bonus utility: Shows at-a-glance backup coverage status.
 * 
 * @param pumperId - Primary pumper ID
 * @returns Backup status summary
 */
export async function getBackupStatusForPumper(pumperId: string): Promise<{
  totalWells: number;
  wellsWithBackup: number;
  wellsWithoutBackup: number;
  activeBackups: number;
  coveragePercentage: number;
  readyForEmergency: boolean;
}> {
  const wells = await getWellsWithBackupInfo(pumperId);
  
  const wellsWithBackup = wells.filter(w => w.backupPumperId).length;
  const wellsWithoutBackup = wells.filter(w => !w.backupPumperId).length;
  const activeBackups = wells.filter(w => w.backupActive).length;
  
  const totalWells = wells.length;
  const coveragePercentage = totalWells > 0 
    ? Math.round((wellsWithBackup / totalWells) * 100) 
    : 100;
  
  return {
    totalWells,
    wellsWithBackup,
    wellsWithoutBackup,
    activeBackups,
    coveragePercentage,
    readyForEmergency: wellsWithoutBackup === 0,
  };
}

/**
 * Set backup pumper for a well.
 * 
 * Bonus utility: Assign or change backup configuration.
 * 
 * @param wellId - Well to configure
 * @param backupPumper - Backup pumper to assign (null to clear)
 * @param options - Operation options
 * @returns Operation result
 */
export async function setBackupForWell(
  wellId: string,
  backupPumper: PumperForAssignment | null,
  options: {
    performedBy: string;
    performedByName: string;
  }
): Promise<BackupOperationResult> {
  const companyId = getCompanyId();
  const path = FIRESTORE_PATHS.wells(companyId);
  
  try {
    // Get well data
    const wellRef = doc(db, path, wellId);
    const wellSnap = await getDoc(wellRef);
    
    if (!wellSnap.exists()) {
      return {
        success: false,
        wellId,
        error: `Well ${wellId} not found`,
      };
    }
    
    const wellData = wellSnap.data();
    const wellName = wellData.name || wellId;
    
    // Validate: backup can't be same as primary
    if (backupPumper && wellData.primaryPumperId === backupPumper.id) {
      return {
        success: false,
        wellId,
        wellName,
        error: 'Backup pumper cannot be the same as primary pumper',
      };
    }
    
    // Update well
    const updateData: Record<string, unknown> = {
      backupPumperId: backupPumper?.id || null,
      backupPumperName: backupPumper?.name || null,
      // If clearing backup, also deactivate it
      ...(backupPumper === null ? { backupActive: false } : {}),
    };
    
    await guardedUpdate(path, wellId, updateData, { userId: options.performedBy });
    
    // Create history entry if changing
    const previousBackup = wellData.backupPumperId;
    if (previousBackup !== backupPumper?.id) {
      await createAssignmentHistoryEntry(wellId, wellName, backupPumper ? 'assigned' : 'unassigned', {
        pumperIds: backupPumper ? [backupPumper.id] : (previousBackup ? [previousBackup] : []),
        pumperNames: backupPumper ? [backupPumper.name] : (wellData.backupPumperName ? [wellData.backupPumperName] : []),
        method: 'backup',
        reason: backupPumper 
          ? `Set as backup pumper (was: ${wellData.backupPumperName || 'none'})`
          : `Removed as backup pumper`,
        performedBy: options.performedBy,
        performedByName: options.performedByName,
        metadata: {
          assignmentType: 'backup',
          previousBackupId: previousBackup,
          previousBackupName: wellData.backupPumperName,
        },
      });
    }
    
    console.log(`🛡️ ${backupPumper ? 'Set' : 'Cleared'} backup for well ${wellName}`);
    
    return {
      success: true,
      wellId,
      wellName,
    };
  } catch (error) {
    console.error(`Failed to set backup for well ${wellId}:`, error);
    return {
      success: false,
      wellId,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Bulk set backup pumper for multiple wells.
 * 
 * Bonus utility: Assign same backup to multiple wells at once.
 * Common pattern: "John backs up all of Mike's wells"
 * 
 * @param wellIds - Wells to configure
 * @param backupPumper - Backup pumper to assign
 * @param options - Operation options
 * @returns Bulk operation result
 */
export async function bulkSetBackup(
  wellIds: string[],
  backupPumper: PumperForAssignment,
  options: {
    performedBy: string;
    performedByName: string;
  }
): Promise<BulkBackupOperationResult> {
  const results: BackupOperationResult[] = [];
  const failures: BackupOperationResult[] = [];
  
  for (const wellId of wellIds) {
    const result = await setBackupForWell(wellId, backupPumper, options);
    results.push(result);
    if (!result.success) {
      failures.push(result);
    }
  }
  
  const successCount = results.filter(r => r.success).length;
  
  console.log(`🛡️ Bulk set backup: ${successCount}/${wellIds.length} succeeded`);
  
  return {
    success: failures.length === 0,
    totalWells: wellIds.length,
    successCount,
    failureCount: failures.length,
    results,
    wellsWithoutBackup: [], // Not relevant for this operation
    failures,
  };
}

/**
 * Get all wells with active backups across the company.
 * 
 * Bonus utility: Dashboard view of all active backup situations.
 * 
 * @returns Wells with active backups
 */
export async function getWellsWithActiveBackups(): Promise<WellWithBackupInfo[]> {
  const companyId = getCompanyId();
  const path = FIRESTORE_PATHS.wells(companyId);
  
  const q = query(
    collection(db, path),
    where('companyId', '==', companyId),
    where('backupActive', '==', true)
  );
  
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(docSnap => {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      name: data.name || docSnap.id,
      companyId: data.companyId,
      primaryPumperId: data.primaryPumperId || null,
      primaryPumperName: data.primaryPumperName || null,
      backupPumperId: data.backupPumperId || null,
      backupPumperName: data.backupPumperName || null,
      backupActive: true,
      assignedPumperIds: data.assignedPumperIds || [],
      status: data.status,
    };
  });
}

/**
 * Count how many wells a pumper is backing up.
 * 
 * Bonus utility: Check if pumper is overloaded as backup.
 * 
 * @param pumperId - Backup pumper ID
 * @returns Count of wells backed up
 */
export async function getBackupWellCount(pumperId: string): Promise<{
  total: number;
  active: number;
  inactive: number;
}> {
  const companyId = getCompanyId();
  const path = FIRESTORE_PATHS.wells(companyId);
  
  const q = query(
    collection(db, path),
    where('companyId', '==', companyId),
    where('backupPumperId', '==', pumperId)
  );
  
  const snapshot = await getDocs(q);
  
  let active = 0;
  let inactive = 0;
  
  for (const docSnap of snapshot.docs) {
    const data = docSnap.data();
    if (data.backupActive) {
      active++;
    } else {
      inactive++;
    }
  }
  
  return {
    total: snapshot.docs.length,
    active,
    inactive,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// WORKLOAD CALCULATION SERVICE (Tasks 61-64)
// ═══════════════════════════════════════════════════════════════════════════════

// Import workload types
import type {
  PumperWorkload,
  WorkloadStatus,
  WorkloadChangeAction,
} from '../../types/wellAssignment';

import {
  WORKLOAD_THRESHOLDS,
  WORKLOAD_STATUS_RECOMMENDATIONS,
  WORKLOAD_SCORE_WEIGHTS,
  getWorkloadStatusFromScore,
  calculatePumperWorkload as buildPumperWorkload,
} from '../../types/wellAssignment';

/**
 * Raw well data needed for workload calculation
 */
interface WellForWorkload {
  id: string;
  name: string;
  status: string;
  priority: number;
  checkFrequency: number; // 1-7 scale (weekly to daily)
  latitude: number;
  longitude: number;
  assignedPumperIds: string[];
  primaryPumperId: string | null;
  backupPumperId: string | null;
  backupActive: boolean;
  temporaryAssignment?: { active: boolean; coveringPumperId: string };
}

/**
 * Workload calculation result with full breakdown
 */
export interface WorkloadCalculationResult {
  /** Full pumper workload data */
  workload: PumperWorkload;
  /** Score breakdown by factor */
  breakdown: {
    wellCountScore: number;
    distanceScore: number;
    frequencyScore: number;
    priorityScore: number;
    rawTotal: number;
    cappedTotal: number;
  };
  /** Raw input values */
  inputs: {
    wellCount: number;
    activeWellCount: number;
    temporaryCoverageCount: number;
    totalDailyMiles: number;
    avgCheckFrequency: number;
    highPriorityWellCount: number;
  };
  /** Wells driving the workload (for debugging/display) */
  wells: Array<{ id: string; name: string; contribution: string }>;
}

/**
 * Options for workload calculation
 */
export interface WorkloadCalculationOptions {
  /** Include wells where pumper is backup (and backup is active) */
  includeActiveBackups?: boolean;
  /** Include wells where pumper is providing temporary coverage */
  includeTemporaryCoverage?: boolean;
  /** Use actual route distance (if available) vs straight-line */
  useRouteDistance?: boolean;
}

/**
 * Workload comparison for team balancing
 */
export interface WorkloadComparison {
  pumperId: string;
  pumperName: string;
  workloadScore: number;
  workloadStatus: WorkloadStatus;
  wellCount: number;
  canTakeMore: boolean;
  capacityRemaining: number; // Wells they could take before overload
}

/**
 * Team workload summary
 */
export interface TeamWorkloadSummary {
  totalPumpers: number;
  totalWells: number;
  avgWorkloadScore: number;
  distribution: {
    light: number;
    balanced: number;
    heavy: number;
    overloaded: number;
  };
  mostOverloaded: WorkloadComparison | null;
  mostAvailable: WorkloadComparison | null;
  recommendations: string[];
}

/**
 * Calculate Haversine distance between two points in miles.
 * Used for route distance estimation.
 */
function haversineDistance(
  lat1: number, lon1: number,
  lat2: number, lon2: number
): number {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Estimate total daily route miles using nearest-neighbor heuristic.
 * Returns to start point (circular route).
 */
function estimateRouteMiles(wells: Array<{ latitude: number; longitude: number }>): number {
  if (wells.length === 0) return 0;
  if (wells.length === 1) return 0; // Single well, no travel
  
  // Simple nearest-neighbor route approximation
  const visited = new Set<number>();
  let totalMiles = 0;
  let current = 0;
  visited.add(0);
  
  while (visited.size < wells.length) {
    let nearestDist = Infinity;
    let nearestIdx = -1;
    
    for (let i = 0; i < wells.length; i++) {
      if (!visited.has(i)) {
        const dist = haversineDistance(
          wells[current].latitude, wells[current].longitude,
          wells[i].latitude, wells[i].longitude
        );
        if (dist < nearestDist) {
          nearestDist = dist;
          nearestIdx = i;
        }
      }
    }
    
    if (nearestIdx !== -1) {
      totalMiles += nearestDist;
      visited.add(nearestIdx);
      current = nearestIdx;
    }
  }
  
  // Return to start
  totalMiles += haversineDistance(
    wells[current].latitude, wells[current].longitude,
    wells[0].latitude, wells[0].longitude
  );
  
  return Math.round(totalMiles * 10) / 10; // Round to 1 decimal
}

/**
 * Fetch wells for workload calculation.
 * 
 * @param pumperId - Pumper to calculate workload for
 * @param options - Calculation options
 * @returns Wells to include in calculation
 */
async function fetchWellsForWorkload(
  pumperId: string,
  options: WorkloadCalculationOptions = {}
): Promise<WellForWorkload[]> {
  const companyId = getCompanyId();
  const path = FIRESTORE_PATHS.wells(companyId);
  
  // Primary wells query
  const primaryQuery = query(
    collection(db, path),
    where('companyId', '==', companyId),
    where('primaryPumperId', '==', pumperId)
  );
  
  const primarySnapshot = await getDocs(primaryQuery);
  const wellMap = new Map<string, WellForWorkload>();
  
  // Add primary wells
  for (const docSnap of primarySnapshot.docs) {
    const data = docSnap.data();
    wellMap.set(docSnap.id, {
      id: docSnap.id,
      name: data.name || docSnap.id,
      status: data.status || 'active',
      priority: data.routePriority || data.priority || 5,
      checkFrequency: data.checkFrequency || 1,
      latitude: data.latitude || 0,
      longitude: data.longitude || 0,
      assignedPumperIds: data.assignedPumperIds || [],
      primaryPumperId: data.primaryPumperId,
      backupPumperId: data.backupPumperId,
      backupActive: data.backupActive || false,
      temporaryAssignment: data.temporaryAssignment,
    });
  }
  
  // Optionally include active backup wells
  if (options.includeActiveBackups) {
    const backupQuery = query(
      collection(db, path),
      where('companyId', '==', companyId),
      where('backupPumperId', '==', pumperId),
      where('backupActive', '==', true)
    );
    
    const backupSnapshot = await getDocs(backupQuery);
    for (const docSnap of backupSnapshot.docs) {
      if (!wellMap.has(docSnap.id)) {
        const data = docSnap.data();
        wellMap.set(docSnap.id, {
          id: docSnap.id,
          name: data.name || docSnap.id,
          status: data.status || 'active',
          priority: data.routePriority || data.priority || 5,
          checkFrequency: data.checkFrequency || 1,
          latitude: data.latitude || 0,
          longitude: data.longitude || 0,
          assignedPumperIds: data.assignedPumperIds || [],
          primaryPumperId: data.primaryPumperId,
          backupPumperId: data.backupPumperId,
          backupActive: true,
          temporaryAssignment: data.temporaryAssignment,
        });
      }
    }
  }
  
  // Optionally include temporary coverage wells
  if (options.includeTemporaryCoverage) {
    const coverageQuery = query(
      collection(db, path),
      where('companyId', '==', companyId),
      where('temporaryAssignment.active', '==', true),
      where('temporaryAssignment.coveringPumperId', '==', pumperId)
    );
    
    try {
      const coverageSnapshot = await getDocs(coverageQuery);
      for (const docSnap of coverageSnapshot.docs) {
        if (!wellMap.has(docSnap.id)) {
          const data = docSnap.data();
          wellMap.set(docSnap.id, {
            id: docSnap.id,
            name: data.name || docSnap.id,
            status: data.status || 'active',
            priority: data.routePriority || data.priority || 5,
            checkFrequency: data.checkFrequency || 1,
            latitude: data.latitude || 0,
            longitude: data.longitude || 0,
            assignedPumperIds: data.assignedPumperIds || [],
            primaryPumperId: data.primaryPumperId,
            backupPumperId: data.backupPumperId,
            backupActive: data.backupActive || false,
            temporaryAssignment: data.temporaryAssignment,
          });
        }
      }
    } catch {
      // Nested field queries may not be supported - skip
      console.warn('Could not query temporary coverage wells - index may be missing');
    }
  }
  
  return Array.from(wellMap.values());
}

/**
 * Calculate workload score for a pumper.
 * 
 * Task 61: Main workload calculation function
 * 
 * Uses the formula:
 *   wellCountScore   = min(wellCount × 2, 40)
 *   distanceScore    = min(dailyMiles ÷ 10, 25)
 *   frequencyScore   = min(avgCheckFrequency × 5, 20)
 *   priorityScore    = min(highPriorityWellCount × 3, 15)
 *   TOTAL = min(sum, 100)
 * 
 * @param pumperId - Pumper to calculate workload for
 * @param pumperName - Pumper's display name
 * @param options - Calculation options
 * @returns Full workload calculation with breakdown
 */
export async function calculateWorkloadScore(
  pumperId: string,
  pumperName: string,
  options: WorkloadCalculationOptions = {}
): Promise<WorkloadCalculationResult> {
  // Fetch wells
  const wells = await fetchWellsForWorkload(pumperId, {
    includeActiveBackups: options.includeActiveBackups ?? true,
    includeTemporaryCoverage: options.includeTemporaryCoverage ?? true,
    useRouteDistance: options.useRouteDistance ?? false,
  });
  
  // Calculate raw counts
  const activeStatuses = ['active', 'producing', 'inactive'];
  const activeWells = wells.filter(w => activeStatuses.includes(w.status));
  const highPriorityWells = wells.filter(w => w.priority <= 3);
  const temporaryCoverageWells = wells.filter(
    w => w.temporaryAssignment?.active && w.temporaryAssignment?.coveringPumperId === pumperId
  );
  
  // Calculate averages
  const avgCheckFrequency = wells.length > 0
    ? wells.reduce((sum, w) => sum + w.checkFrequency, 0) / wells.length
    : 0;
  
  // Estimate daily route miles
  const wellsWithCoords = wells.filter(w => w.latitude && w.longitude);
  const totalDailyMiles = estimateRouteMiles(wellsWithCoords);
  
  // Calculate scores
  const wellCountScore = Math.min(wells.length * WORKLOAD_SCORE_WEIGHTS.wellCount.multiplier, WORKLOAD_SCORE_WEIGHTS.wellCount.maxScore);
  const distanceScore = Math.min(totalDailyMiles * WORKLOAD_SCORE_WEIGHTS.distance.multiplier, WORKLOAD_SCORE_WEIGHTS.distance.maxScore);
  const frequencyScore = Math.min(avgCheckFrequency * WORKLOAD_SCORE_WEIGHTS.frequency.multiplier, WORKLOAD_SCORE_WEIGHTS.frequency.maxScore);
  const priorityScore = Math.min(highPriorityWells.length * WORKLOAD_SCORE_WEIGHTS.priority.multiplier, WORKLOAD_SCORE_WEIGHTS.priority.maxScore);
  
  const rawTotal = wellCountScore + distanceScore + frequencyScore + priorityScore;
  const cappedTotal = Math.min(Math.round(rawTotal), 100);
  
  // Build full workload object
  const workload = buildPumperWorkload(
    pumperId,
    pumperName,
    wells.length,
    activeWells.length,
    temporaryCoverageWells.length,
    totalDailyMiles,
    avgCheckFrequency,
    highPriorityWells.length
  );
  
  // Build well contribution list
  const wellContributions = wells.map(w => {
    const contributions: string[] = [];
    contributions.push('+2 (well count)');
    if (w.priority <= 3) contributions.push('+3 (high priority)');
    if (w.checkFrequency >= 5) contributions.push('+high freq');
    return {
      id: w.id,
      name: w.name,
      contribution: contributions.join(', '),
    };
  });
  
  return {
    workload,
    breakdown: {
      wellCountScore: Math.round(wellCountScore * 10) / 10,
      distanceScore: Math.round(distanceScore * 10) / 10,
      frequencyScore: Math.round(frequencyScore * 10) / 10,
      priorityScore: Math.round(priorityScore * 10) / 10,
      rawTotal: Math.round(rawTotal * 10) / 10,
      cappedTotal,
    },
    inputs: {
      wellCount: wells.length,
      activeWellCount: activeWells.length,
      temporaryCoverageCount: temporaryCoverageWells.length,
      totalDailyMiles,
      avgCheckFrequency: Math.round(avgCheckFrequency * 10) / 10,
      highPriorityWellCount: highPriorityWells.length,
    },
    wells: wellContributions,
  };
}

/**
 * Get workload status with full context.
 * 
 * Task 62: Status with thresholds and recommendations
 * 
 * Thresholds:
 * - light: 0-29 (can take more wells)
 * - balanced: 30-60 (optimal)
 * - heavy: 61-80 (nearing capacity)
 * - overloaded: 81-100 (needs redistribution)
 * 
 * @param pumperId - Pumper to check
 * @param pumperName - Pumper's display name
 * @returns Status with recommendations
 */
export async function getWorkloadStatus(
  pumperId: string,
  pumperName: string
): Promise<{
  status: WorkloadStatus;
  score: number;
  label: string;
  emoji: string;
  description: string;
  canAssign: boolean;
  warning: string | null;
  suggestion: string;
  thresholds: typeof WORKLOAD_THRESHOLDS;
  breakdown: WorkloadCalculationResult['breakdown'];
}> {
  const result = await calculateWorkloadScore(pumperId, pumperName);
  const status = result.workload.workloadStatus;
  const recommendation = WORKLOAD_STATUS_RECOMMENDATIONS[status];
  
  // Get emoji and labels
  const emojiMap: Record<WorkloadStatus, string> = {
    light: '🟢',
    balanced: '🟡',
    heavy: '🟠',
    overloaded: '🔴',
  };
  
  const descriptionMap: Record<WorkloadStatus, string> = {
    light: 'This pumper has capacity for additional wells',
    balanced: 'This pumper has an optimal workload',
    heavy: 'This pumper is nearing capacity — assign with caution',
    overloaded: 'This pumper is overloaded — consider redistributing wells',
  };
  
  return {
    status,
    score: result.workload.workloadScore,
    label: status.charAt(0).toUpperCase() + status.slice(1),
    emoji: emojiMap[status],
    description: descriptionMap[status],
    canAssign: recommendation.canAssign,
    warning: recommendation.warning,
    suggestion: recommendation.suggestion,
    thresholds: WORKLOAD_THRESHOLDS,
    breakdown: result.breakdown,
  };
}

/**
 * Get workload recommendations for a pumper.
 * 
 * Task 63: Intelligent recommendations based on workload analysis
 * 
 * @param pumperId - Pumper to analyze
 * @param pumperName - Pumper's display name
 * @returns Actionable recommendations
 */
export async function getWorkloadRecommendations(
  pumperId: string,
  pumperName: string
): Promise<{
  pumper: { id: string; name: string };
  workload: PumperWorkload;
  recommendations: Array<{
    priority: 'high' | 'medium' | 'low';
    action: WorkloadChangeAction;
    description: string;
    impact: string;
    wellCount?: number;
    targetPumper?: { id: string; name: string };
  }>;
  summary: string;
}> {
  const result = await calculateWorkloadScore(pumperId, pumperName);
  const workload = result.workload;
  const recommendations: Array<{
    priority: 'high' | 'medium' | 'low';
    action: WorkloadChangeAction;
    description: string;
    impact: string;
    wellCount?: number;
    targetPumper?: { id: string; name: string };
  }> = [];
  
  // Analyze workload and generate recommendations
  
  // CRITICAL: Overloaded pumpers
  if (workload.workloadStatus === 'overloaded') {
    const wellsToRemove = Math.ceil(workload.wellCount * 0.25);
    recommendations.push({
      priority: 'high',
      action: 'remove_wells',
      description: `Remove ${wellsToRemove} wells to reduce overload`,
      impact: `Would reduce workload score by ~${wellsToRemove * 2} points`,
      wellCount: wellsToRemove,
    });
    
    if (workload.temporaryCoverageCount > 0) {
      recommendations.push({
        priority: 'high',
        action: 'rebalance',
        description: `Consider ending ${workload.temporaryCoverageCount} temporary coverage assignments`,
        impact: 'Would return wells to original pumpers, reducing immediate load',
        wellCount: workload.temporaryCoverageCount,
      });
    }
  }
  
  // HIGH PRIORITY: Heavy workload with high-priority wells
  if (workload.workloadStatus === 'heavy' && workload.highPriorityWellCount > 5) {
    recommendations.push({
      priority: 'high',
      action: 'rebalance',
      description: 'High concentration of priority wells — consider distributing',
      impact: 'Would reduce stress and improve response time',
      wellCount: Math.ceil(workload.highPriorityWellCount / 2),
    });
  }
  
  // MEDIUM: Heavy workload in general
  if (workload.workloadStatus === 'heavy' && recommendations.length === 0) {
    recommendations.push({
      priority: 'medium',
      action: 'remove_wells',
      description: 'Workload is heavy — monitor for burnout risk',
      impact: 'No immediate action needed, but avoid adding more wells',
    });
  }
  
  // MEDIUM: High daily mileage
  if (result.breakdown.distanceScore >= 20) {
    recommendations.push({
      priority: 'medium',
      action: 'rebalance',
      description: `High daily mileage (${result.inputs.totalDailyMiles} miles) — route may be inefficient`,
      impact: 'Reassigning geographically distant wells could save 20-30% drive time',
    });
  }
  
  // MEDIUM: Temporary coverage creating strain
  if (workload.temporaryCoverageCount >= 3 && workload.workloadStatus !== 'light') {
    recommendations.push({
      priority: 'medium',
      action: 'rebalance',
      description: `Carrying ${workload.temporaryCoverageCount} temporary coverage wells`,
      impact: 'Consider permanent reassignment if coverage will extend',
    });
  }
  
  // LOW: Light workload - can take more
  if (workload.workloadStatus === 'light') {
    const capacityForMore = Math.floor((30 - workload.workloadScore) / 2);
    recommendations.push({
      priority: 'low',
      action: 'add_wells',
      description: `Has capacity for ~${capacityForMore} additional wells`,
      impact: 'Good candidate when rebalancing overloaded pumpers',
      wellCount: capacityForMore,
    });
  }
  
  // Generate summary
  let summary: string;
  if (workload.workloadStatus === 'overloaded') {
    summary = `🔴 ${pumperName} is critically overloaded at ${workload.workloadScore}/100. Immediate action recommended.`;
  } else if (workload.workloadStatus === 'heavy') {
    summary = `🟠 ${pumperName} is running heavy at ${workload.workloadScore}/100. Monitor and avoid adding wells.`;
  } else if (workload.workloadStatus === 'balanced') {
    summary = `🟡 ${pumperName} has optimal workload at ${workload.workloadScore}/100. No changes needed.`;
  } else {
    summary = `🟢 ${pumperName} has light workload at ${workload.workloadScore}/100. Good candidate for additional assignments.`;
  }
  
  return {
    pumper: { id: pumperId, name: pumperName },
    workload,
    recommendations,
    summary,
  };
}

/**
 * Get workload comparison for all pumpers in the company.
 * 
 * Bonus utility: Team-wide workload view for managers
 * 
 * @returns Array of pumper workloads for comparison
 */
export async function getTeamWorkloadComparison(): Promise<WorkloadComparison[]> {
  const companyId = getCompanyId();
  
  // Get all active users (pumpers, admins, managers, etc.)
  // This ensures we can assign wells to anyone if needed
  const pumpers = await getUsersByCompany({ 
    isActive: true 
  });
  
  const pumperMap = new Map<string, { name: string; wells: number }>();
  
  // Initialize map with all pumpers (0 wells initially)
  for (const pumper of pumpers) {
    const firstName = pumper.firstName || '';
    const lastName = pumper.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();

    pumperMap.set(pumper.id, { 
      name: fullName || pumper.email || 'Unknown User', 
      wells: 0 
    });
  }
  
  // Get well counts
  const path = FIRESTORE_PATHS.wells(companyId);
  const wellsQuery = query(
    collection(db, path),
    where('companyId', '==', companyId),
    where('status', 'in', ['active', 'producing', 'inactive'])
  );
  
  const snapshot = await getDocs(wellsQuery);
  
  // Count wells per pumper
  for (const docSnap of snapshot.docs) {
    const data = docSnap.data();
    const pumperId = data.primaryPumperId;
    
    if (pumperId) {
      const existing = pumperMap.get(pumperId);
      if (existing) {
        existing.wells++;
      } else {
        // Handle case where pumper exists on well but not in users list (e.g. deleted user)
        // We include them so the workload is accurate, but they might not have full details
        pumperMap.set(pumperId, { 
          name: data.primaryPumperName || 'Unknown Pumper', 
          wells: 1 
        });
      }
    }
  }
  
  // Calculate workload for each pumper
  const comparisons: WorkloadComparison[] = [];
  
  for (const [pumperId, info] of pumperMap) {
    const result = await calculateWorkloadScore(pumperId, info.name);
    const workload = result.workload;
    
    // Calculate capacity remaining (wells until overload threshold of 80)
    const pointsUntilOverload = 80 - workload.workloadScore;
    const capacityRemaining = Math.max(0, Math.floor(pointsUntilOverload / 2));
    
    comparisons.push({
      pumperId,
      pumperName: info.name,
      workloadScore: workload.workloadScore,
      workloadStatus: workload.workloadStatus,
      wellCount: workload.wellCount,
      canTakeMore: workload.workloadStatus === 'light' || workload.workloadStatus === 'balanced',
      capacityRemaining,
    });
  }
  
  // Sort by workload score descending (most overloaded first)
  comparisons.sort((a, b) => b.workloadScore - a.workloadScore);
  
  return comparisons;
}

/**
 * Get team workload summary with recommendations.
 * 
 * Bonus utility: Executive dashboard view
 * 
 * @returns Team-wide summary with actionable insights
 */
export async function getTeamWorkloadSummary(): Promise<TeamWorkloadSummary> {
  const comparisons = await getTeamWorkloadComparison();
  
  if (comparisons.length === 0) {
    return {
      totalPumpers: 0,
      totalWells: 0,
      avgWorkloadScore: 0,
      distribution: { light: 0, balanced: 0, heavy: 0, overloaded: 0 },
      mostOverloaded: null,
      mostAvailable: null,
      recommendations: ['No pumpers with assigned wells found'],
    };
  }
  
  // Calculate totals
  const totalPumpers = comparisons.length;
  const totalWells = comparisons.reduce((sum, c) => sum + c.wellCount, 0);
  const avgWorkloadScore = Math.round(
    comparisons.reduce((sum, c) => sum + c.workloadScore, 0) / totalPumpers
  );
  
  // Count distribution
  const distribution = {
    light: comparisons.filter(c => c.workloadStatus === 'light').length,
    balanced: comparisons.filter(c => c.workloadStatus === 'balanced').length,
    heavy: comparisons.filter(c => c.workloadStatus === 'heavy').length,
    overloaded: comparisons.filter(c => c.workloadStatus === 'overloaded').length,
  };
  
  // Find extremes
  const mostOverloaded = comparisons.find(c => c.workloadStatus === 'overloaded') || 
    comparisons.find(c => c.workloadStatus === 'heavy') || null;
  const sortedByCapacity = [...comparisons].sort((a, b) => b.capacityRemaining - a.capacityRemaining);
  const mostAvailable = sortedByCapacity[0]?.canTakeMore ? sortedByCapacity[0] : null;
  
  // Generate recommendations
  const recommendations: string[] = [];
  
  if (distribution.overloaded > 0) {
    recommendations.push(`🔴 CRITICAL: ${distribution.overloaded} pumper(s) overloaded — redistribute wells immediately`);
  }
  
  if (distribution.heavy > distribution.light) {
    recommendations.push(`⚠️ Team is running heavy — consider hiring or route optimization`);
  }
  
  if (mostOverloaded && mostAvailable) {
    const canTransfer = Math.min(
      Math.ceil(mostOverloaded.wellCount * 0.2),
      mostAvailable.capacityRemaining
    );
    if (canTransfer > 0) {
      recommendations.push(
        `💡 Transfer ${canTransfer} wells from ${mostOverloaded.pumperName} to ${mostAvailable.pumperName}`
      );
    }
  }
  
  const avgWellsPerPumper = Math.round(totalWells / totalPumpers);
  if (distribution.overloaded === 0 && distribution.heavy <= 1) {
    recommendations.push(`✅ Team workload is healthy (avg ${avgWellsPerPumper} wells/pumper)`);
  }
  
  return {
    totalPumpers,
    totalWells,
    avgWorkloadScore,
    distribution,
    mostOverloaded,
    mostAvailable,
    recommendations,
  };
}

/**
 * Quick workload check for assignment dialogs.
 * 
 * Bonus utility: Fast check before assigning a well
 * 
 * @param pumperId - Pumper to check
 * @returns Quick status for UI
 */
export async function quickWorkloadCheck(pumperId: string): Promise<{
  canAssign: boolean;
  status: WorkloadStatus;
  score: number;
  warning: string | null;
}> {
  // Get well count directly for speed
  const companyId = getCompanyId();
  const path = FIRESTORE_PATHS.wells(companyId);
  
  const q = query(
    collection(db, path),
    where('companyId', '==', companyId),
    where('primaryPumperId', '==', pumperId)
  );
  
  const snapshot = await getDocs(q);
  const wellCount = snapshot.docs.length;
  
  // Quick score estimate (just well count factor)
  const quickScore = Math.min(wellCount * 2, 40);
  const status = getWorkloadStatusFromScore(quickScore);
  
  const recommendation = WORKLOAD_STATUS_RECOMMENDATIONS[status];
  
  return {
    canAssign: recommendation.canAssign,
    status,
    score: quickScore,
    warning: recommendation.warning,
  };
}

/**
 * Get workload trend for a pumper over time.
 * 
 * Bonus utility: Historical analysis (would require storing snapshots)
 * Currently returns current state with placeholder trend.
 */
export async function getWorkloadTrend(
  pumperId: string,
  pumperName: string
): Promise<{
  current: PumperWorkload;
  trend: 'increasing' | 'stable' | 'decreasing';
  changeRate: number; // Wells added/removed per week (placeholder)
}> {
  const result = await calculateWorkloadScore(pumperId, pumperName);
  
  // TODO: Implement actual trend tracking with history collection
  // For now, return stable with current data
  return {
    current: result.workload,
    trend: 'stable',
    changeRate: 0,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// UTILITY EXPORTS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Service version and metadata
 */
export const WELL_ASSIGNMENT_SERVICE_VERSION = '1.0.0';

/**
 * Check if assignment service is available
 */
export function isAssignmentServiceAvailable(): boolean {
  try {
    const companyId = getCompanyId();
    return !!companyId && companyId !== 'undefined';
  } catch {
    return false;
  }
}
